import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from "../../Context/auth";

// =========================================================
// --- FUNÇÕES AUXILIARES DE CONVERSÃO (Global) ---
// =========================================================

const formatarDataSegura = (data) => {
    if (!data) return ' - ';
    const dataObj = data instanceof Date ? data : new Date(data);
    return dataObj.toLocaleDateString('pt-BR');
};

const mapToPostgresItem = (item) => {

    return {
        // Converte o ID para Número. Se for string vazia ou 0, vira null.
        id: item.id || null, // Garante que o ID seja enviado para o backend
        produto_id: Number(item.produtoId) || null,
        grade_id: Number(item.grade) || null,
        fornecedor_id: Number(item.fornecedorId) || null,

        // Campos que já são tratados no handleAdicionarItem (mas vamos garantir)
        quantidade_volumes: Number(item.quantidade) || 0,
        quantidade_total: Number(item.quantidadeTotal) || 0,
        preco_venda: Number(item.precoVenda) || 0,
        preco_compra: Number(item.precoCompra) || 0,
        valor_subtotal: Number(item.subtotal) || 0,
        valor_total: Number(item.subtotal) || 0, // valor_total deve ser o subtotal do item

        // Campos que podem ser nulos ou ter valores
        cor: item.cor || null,
        tipo: item.tipo || null,
        tamanhos_do_item: item.tamanhosDoItem || [],

        // Campos com valores padrão
        acrescimos: 0,
        desconto: 0,
        qt_grade: Number(item.qt_grade) || 0, // Veio do item

        // Campos que o backend deve preencher (se necessário)
        pedido_id: item.pedidoId || null,

        financeiroAReceber: item.financeiroAReceber || null,
        financeiroAPagar: item.financeiroAPagar || null
    };
};

const mapFromPostgresItem = (dbItem) => ({
    id: dbItem.id,
    pedidoId: dbItem.pedido_id,
    produtoId: dbItem.produto_id,
    grade: dbItem.grade_id,
    fornecedorId: dbItem.fornecedor_id,
    cor: dbItem.cor,
    quantidade: dbItem.quantidade_volumes,
    quantidadeTotal: dbItem.quantidade_total,
    precoVenda: dbItem.preco_venda,
    precoCompra: dbItem.preco_compra,
    subtotal: dbItem.valor_subtotal,
    tamanhosDoItem: dbItem.tamanhos_do_item,
    financeiroAReceber: dbItem.financeiro_a_receber_id,
    financeiroAPagar: dbItem.financeiro_a_pagar_id,
    status: dbItem.status, // Mapeando status vindo do banco
});


const CurrencyInput = ({ label, value, ...props }) => (
    <div>
        <label className="form-label">{label}</label>
        <input
            type="text"
            className="form-control"
            value={Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            disabled
            {...props}
        />
    </div>
);

// =========================================================
// --- COMPONENTES: Modal de Configuração/Visualização de Tamanhos (Mantido) ---
// =========================================================

const TamanhoModal = ({ isVisible, onClose, tamanhosData, onSave, gradeNome }) => {
    const [tamanhosEmEdicao, setTamanhosEmEdicao] = useState(tamanhosData);

    useEffect(() => {
        setTamanhosEmEdicao(tamanhosData);
    }, [tamanhosData]);

    if (!isVisible) return null;

    const handleUpdateQtde = (id, newQtde) => {
        const qtde = Number(newQtde.replace(/[^0-9]/g, ''));
        if (isNaN(qtde) || qtde < 0) return;

        setTamanhosEmEdicao(prev => prev.map(item =>
            item.id === id ? { ...item, quantidade: qtde } : item
        ));
    };

    const somatorio = tamanhosEmEdicao.reduce((sum, item) => sum + (Number(item.quantidade) || 0), 0);

    const handleSave = () => {
        if (somatorio === 0) {
            Swal.fire("Atenção", "A quantidade total deve ser maior que zero.", "warning");
            return;
        }
        onSave(tamanhosEmEdicao, somatorio);
    };

    // Estilos (Mantidos - Layout Overlay)
    const modalStyle = {
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center',
        zIndex: 1060
    };
    const contentStyle = {
        backgroundColor: 'white', padding: '20px', borderRadius: '12px', width: '90%', maxWidth: '500px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
    };

    return (
        <div style={modalStyle}>
            <div style={contentStyle}>
                <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                    <h5 className="m-0 fw-bold" style={{ color: '#2185c6' }}>Quantidade por tamanho</h5>
                    <button type="button" className="btn-close" onClick={onClose}></button>
                </div>
                <div className="modal-body p-0" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <div className="d-flex justify-content-between mb-2 fw-bold text-muted px-2">
                        <span>Tamanho</span>
                        <span>Quantidade</span>
                    </div>

                    {tamanhosEmEdicao.map(item => (
                        <div key={item.id} className="d-flex justify-content-between align-items-center mb-2 p-2 border rounded bg-light">
                            <span className="fw-bold">{item.tamanho}</span>
                            <input
                                type="number"
                                className="form-control-custom text-center"
                                value={item.quantidade}
                                onChange={(e) => handleUpdateQtde(item.id, e.target.value)}
                                min="0"
                                style={{ width: '80px', padding: '4px' }}
                            />
                        </div>
                    ))}
                </div>
                <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                    <span className="fw-bold fs-5">Total: <span style={{ color: '#2185c6' }}>{somatorio}</span></span>
                    <div>
                        <button type="button" className="btn-secondary-custom me-2" onClick={onClose}>Cancelar</button>
                        <button type="button" className="btn-primary-custom" onClick={handleSave}>Salvar</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const VisualizarGradeModal = ({ isVisible, onClose, tamanhosData, gradeNome }) => {
    if (!isVisible) return null;

    const total = tamanhosData.reduce((sum, item) => sum + (Number(item.quantidade) || 0), 0);

    const modalStyle = {
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
        justifyContent: 'center', alignItems: 'center', zIndex: 1060
    };
    const contentStyle = {
        backgroundColor: 'white', padding: '20px', borderRadius: '12px',
        width: '90%', maxWidth: '500px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
    };

    return (
        <div style={modalStyle}>
            <div style={contentStyle}>
                <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                    <h5 className="m-0 fw-bold" style={{ color: '#2185c6' }}>Grade: {gradeNome}</h5>
                    <button type="button" className="btn-close" onClick={onClose}></button>
                </div>
                <div className="modal-body p-0" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <div className="d-flex justify-content-between mb-2 fw-bold text-muted px-2">
                        <span>Tamanho</span>
                        <span>Quantidade</span>
                    </div>
                    {tamanhosData.map(item => (
                        <div key={item.id} className="d-flex justify-content-between align-items-center mb-2 p-2 border rounded bg-light">
                            <span className="fw-bold">{item.tamanho}</span>
                            <span className="fw-bold fs-6">{item.quantidade}</span>
                        </div>
                    ))}
                </div>
                <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                    <span className="fw-bold fs-5">Total: <span style={{ color: '#2185c6' }}>{total}</span></span>
                    <button type="button" className="btn-secondary-custom" onClick={onClose}>Fechar</button>
                </div>
            </div>
        </div>
    );
};

// =========================================================
// --- COMPONENTE PRINCIPAL ---
// =========================================================

// ================== CORREÇÃO AQUI ==================
// 1. Adicionada a prop `onImprimirRomaneio`
function PedidoForm({
    isEditing = false,
    initialData,
    pedidoId,
    onSubmit,
    tenantId,
    numeroPedido,
    onImprimirPedido,
    onImprimirRomaneio, // <-- PROP ADICIONADA
    plano = 'Representante', // <-- PROP ADICIONADA COM DEFAULT
    loading
}) {
    const { currentUser, appUserData } = useAuth();

    const isPlanoRepresentante = plano?.toLowerCase() === 'representante';
    // --- ESTADOS DO FORMULÁRIO ---
    const [carregando, setCarregando] = useState(true);
    const [tipo, setTipo] = useState('Representacao'); // Novo estado: 'Representacao' ou 'Estoque'
    const [activeTab, setActiveTab] = useState('dados');
    const [faturamentoTab, setFaturamentoTab] = useState('receber');
    const [dataCadastro, setDataCadastro] = useState(new Date().toISOString().split('T')[0]);
    const [status, setStatus] = useState('Em Aberto');
    const [clienteId, setClienteId] = useState('');
    const [notaFiscal, setNotaFiscal] = useState('');
    const [transportadoraId, setTransportadoraId] = useState('');
    const [itensDoPedido, setItensDoPedido] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [transportadoras, setTransportadoras] = useState([]);
    const [produtos, setProdutos] = useState([]);
    const [fornecedores, setFornecedores] = useState([]);
    const [terceiros, setTerceiros] = useState([]);
    const [todasAsGrades, setTodasAsGrades] = useState([]);
    const [enderecoCliente, setEnderecoCliente] = useState(null);
    const [produtoSelecionadoId, setProdutoSelecionadoId] = useState('');
    const [produtoInfo, setProdutoInfo] = useState(null);
    const [fornecedorNome, setFornecedorNome] = useState('');
    const [fornecedorSelecionadoId, setFornecedorSelecionadoId] = useState(''); // NOVO: Para o Selectbox
    const [coresDisponiveis, setCoresDisponiveis] = useState([]);
    const [gerandoPagarId, setGerandoPagarId] = useState(null);

    // --- ESTADOS DE GRADE/TAMANHOS ---
    const [gradesDisponiveis, setGradesDisponiveis] = useState([]);
    const [gradeSelecionada, setGradeSelecionada] = useState('');
    const [tamanhosGradeAtual, setTamanhosGradeAtual] = useState([]);
    const [isTamanhoModalVisible, setIsTamanhoModalVisible] = useState(false);
    const [tamanhosConfigurados, setTamanhosConfigurados] = useState([]);

    const [corSelecionada, setCorSelecionada] = useState('');
    const [quantidade, setQuantidade] = useState(1);
    const [precoVenda, setPrecoVenda] = useState(0);
    const [precoCompra, setPrecoCompra] = useState(0);
    const [qtGrade, setQtGrade] = useState(0);

    // --- ESTADO PARA CONTROLE DE ALTERAÇÕES (DIRTY) ---
    const [isDirty, setIsDirty] = useState(false);
    const [initialDataState, setInitialDataState] = useState(null);

    // MOVENDO DECLARAÇÕES DE ESTADO PARA ANTES DO USEEFFECT

    const [totalQtGrade, setTotalQtGrade] = useState(0);
    const [totalVol, setTotalVol] = useState(0);
    const [financeiroReceberList, setFinanceiroReceberList] = useState([]);
    const [financeiroPagarList, setFinanceiroPagarList] = useState([]);
    const [despesaTerceiroId, setDespesaTerceiroId] = useState('');
    const [despesaDescricao, setDespesaDescricao] = useState('');
    const [despesaValor, setDespesaValor] = useState(0);
    const [despesaData] = useState(new Date().toISOString().split('T')[0]);
    const [outrasDespesasList, setOutrasDespesasList] = useState([]);
    const [acrescimos, setAcrescimos] = useState(0);
    const [frete, setFrete] = useState(0);
    const [totalVenda, setTotalVenda] = useState(0);
    const [totalCompra, setTotalCompra] = useState(0);
    const [formaPagamentoReceber, setFormaPagamentoReceber] = useState('PIX');
    const [prazoReceber, setPrazoReceber] = useState('0');
    // Adicione outros estados que possam estar faltando aqui se usados no useEffect
    const [tamanhosParaVisualizar, setTamanhosParaVisualizar] = useState([]);
    const [gradeVisualizarNome, setGradeVisualizarNome] = useState('');
    const [acrescimosInput, setAcrescimosInput] = useState('');
    const [freteInput, setFreteInput] = useState('');
    const [prazoPagar, setPrazoPagar] = useState('0');
    const [formaPagamentoPagar, setFormaPagamentoPagar] = useState('PIX');
    const [movimentarEstoque, setMovimentarEstoque] = useState(false); // Novo estado
    const hasModuloEstoque = appUserData && appUserData.modulos && appUserData.modulos.includes(2);

    const [processingAction, setProcessingAction] = useState(false);

    const mapFromPostgresFinanceiro = (dbFin) => ({
        id: dbFin.id,
        pedidoId: dbFin.pedido_id,
        fornecedorId: dbFin.empresa_id || dbFin.fornecedor_id, // Fallback para garantir leitura
        fornecedorNome: dbFin.fornecedorNome || dbFin.cliente_nome,
        valorTotal: Number(dbFin.valor_total),
        formaPagamento: dbFin.forma_pagamento,
        status: dbFin.status,
        dataCadastro: dbFin.data_cadastro,
        dataVencimento: dbFin.data_vencimento,
        dataPagamento: dbFin.data_pagamento,
        numeroDoc: dbFin.numero_doc || '', // Mapeado para verificação de vínculo
        descricao: dbFin.descricao || '',
    });

    useEffect(() => {
        if (isEditing && initialData) {
            setDataCadastro(new Date(initialData.data_cadastro).toISOString().split('T')[0]);
            setStatus(initialData.status);
            setClienteId(initialData.cliente_id);
            setTransportadoraId(initialData.transportadora_id || '');
            setNotaFiscal(initialData.nota_fiscal || '');
            setAcrescimos(initialData.acrescimos || 0);
            setNotaFiscal(initialData.nota_fiscal || '');
            setAcrescimos(initialData.acrescimos || 0);
            setFrete(initialData.frete || 0);
            setFrete(initialData.frete || 0);
            setTipo(initialData.tipo || 'Representacao'); // Carrega tipo existente
            // Sincroniza o estado do checkbox com os dados carregados
            setMovimentarEstoque(hasModuloEstoque ? (initialData.movimentar_estoque !== false) : false);

            let currentMappedItems = [];

            const itensData = initialData.itensDoPedido || [];
            // Certifica que produtos e fornecedores estão carregados antes de mapear
            if (produtos.length > 0 && fornecedores.length > 0) {
                currentMappedItems = itensData.map(item => ({
                    ...mapFromPostgresItem(item),
                    descricao: produtos.find(p => p.id === item.produto_id)?.descricao || 'N/A',
                    fornecedorNome: fornecedores.find(f => f.id === item.fornecedor_id)?.nome || 'N/A',
                }));

                setItensDoPedido(currentMappedItems);
            }

            setFinanceiroReceberList(
                (initialData.financeiroReceberList || []).map(mapFromPostgresFinanceiro)
            );

            const todosPagar = (initialData.financeiroPagarList || []).map(mapFromPostgresFinanceiro);

            // Cria um Set com IDs de pagamentos vinculados a itens (convertendo para número para garantir match)
            const idsVinculadosAoPedido = new Set(
                itensData.map(item => Number(item.financeiro_a_pagar_id)).filter(id => id > 0)
            );

            // A Pagar: Somente aqueles vinculados a itens
            // Se o vínculo estiver falhando no backend, precisamos corrigir LÁ, não remover o filtro aqui (pois mistura com despesas)
            setFinanceiroPagarList(todosPagar.filter(p => idsVinculadosAoPedido.has(Number(p.id))));

            // Outras Despesas: Aqueles NÃO vinculados a itens
            setOutrasDespesasList(todosPagar.filter(p => !idsVinculadosAoPedido.has(Number(p.id))));

            if (clientes.length > 0) {
                const clienteAtual = clientes.find(c => c.id === initialData.cliente_id);
                setEnderecoCliente(clienteAtual || null);
            }

            // Define o estado inicial para comparação (dirty check)
            // Normaliza os dados para garantir comparação correta
            setInitialDataState({
                dataCadastro: new Date(initialData.data_cadastro).toISOString().split('T')[0],
                status: initialData.status,
                clienteId: String(initialData.cliente_id),
                transportadoraId: String(initialData.transportadora_id || ''),
                notaFiscal: String(initialData.nota_fiscal || ''),
                acrescimos: Number(initialData.acrescimos || 0),
                frete: Number(initialData.frete || 0),
                itensDoPedido: JSON.parse(JSON.stringify(currentMappedItems || [])), // Deep copy
                tipo: initialData.tipo || 'Representacao',
                movimentarEstoque: hasModuloEstoque ? (initialData.movimentar_estoque !== false) : false // Lógica igual ao useState
            });
        }
        // Modificado para re-executar QUANDO os dados (produtos, fornecedores) chegarem
    }, [isEditing, initialData, produtos, fornecedores, terceiros, clientes, hasModuloEstoque]);



    // --- EFEITO PARA CHECK DIRTY ---
    useEffect(() => {
        if (!initialDataState) return;

        const currentSnapshot = {
            dataCadastro: dataCadastro,
            status: status,
            clienteId: String(clienteId),
            transportadoraId: String(transportadoraId || ''),
            notaFiscal: String(notaFiscal || ''),
            acrescimos: Number(acrescimos || 0),
            frete: Number(frete || 0),

            itensDoPedido: itensDoPedido, // Assumindo que a estrutura é mantida
            tipo: tipo,
            movimentarEstoque: movimentarEstoque
        };

        // Helper para normalizar: undefined virou null? null virou undefined?
        // JSON.stringify remove undefined. Se initial tinha null e agora undefined, muda string.
        // Vamos forçar undefined -> null para garantir
        const replacer = (key, value) => (value === undefined ? null : value);

        const strCurrent = JSON.stringify(currentSnapshot, replacer);
        const strInitial = JSON.stringify(initialDataState, replacer);

        const hasChanged = strCurrent !== strInitial;

        // DEBUG: Descobrir o que está diferente
        if (hasChanged) {
            // console.log("--- DETECTADA ALTERAÇÃO (isDirty = true) ---");
            // Campos simples
            const keys = Object.keys(currentSnapshot);
            let diffFound = false;
            keys.forEach(key => {
                const vNow = JSON.stringify(currentSnapshot[key], replacer);
                const vIni = JSON.stringify(initialDataState[key], replacer);
                if (vNow !== vIni) {
                    // console.log(`Diferença em '${key}':`, "\nAtual:", vNow, "\nInicial:", vIni);
                    diffFound = true;
                }
            });
            if (!diffFound) console.log("Diferença pode estar na ordem das chaves ou estrutura profunda escondida?");
        }

        setIsDirty(hasChanged);

    }, [dataCadastro, status, clienteId, transportadoraId, notaFiscal, acrescimos, frete, itensDoPedido, tipo, movimentarEstoque, initialDataState]);

    const apiCall = useCallback(async (url, method = 'GET', data = null) => {
        try {
            const token = await currentUser?.getIdToken();
            if (!token) {
                throw new Error("Unauthorized: Token de autenticação não disponível.");
            }

            const API_URL = process.env.REACT_APP_API_URL;
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            };

            const config = { method, headers };
            if (data) config.body = JSON.stringify(data);

            const response = await fetch(`${API_URL}${url}`, config);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: `Erro na requisição: ${response.statusText}` }));
                throw new Error(`Erro na requisição: ${response.status} - ${errorData.message || response.statusText}`);
            }
            if (response.status !== 204) {
                return response.json();
            }
            return null;
        } catch (error) {
            console.error(`Falha no apiCall para ${url}:`, error.message || error);
            throw error;
        }
    }, [currentUser]);

    // --- NOVO: Carrega configuração de movimentação de estoque E Tipo Padrão ---
    useEffect(() => {
        if (!isEditing) {
            // Se for novo pedido, busca configurações
            apiCall('/api/configuracoes')
                .then(config => {
                    if (config) {
                        // Define tipo padrão, RESPEITANDO o módulo
                        if (config.tipo_pedido_padrao) {
                            if (config.tipo_pedido_padrao === 'Estoque' && !hasModuloEstoque) {
                                setTipo('Representacao');
                            } else {
                                setTipo(config.tipo_pedido_padrao);
                            }
                        }

                        // Define checkbox de estoque (Apenas se o módulo estiver ativo E o tipo permitir)
                        // Logica refinada abaixo no useEffect de dependência [tipo]
                    }
                })
                .catch(err => console.error("Erro ao carregar configurações:", err));
        } else if (isEditing && initialData) {
            // Se editando, já carregou do initialData no outro useEffect
        }
    }, [isEditing, apiCall, initialData, hasModuloEstoque]);

    // Safety: Se o usuário não tem módulo de estoque, mas o tipo está como Estoque (ex: edição de pedido antigo), força Representacao
    useEffect(() => {
        if (!hasModuloEstoque && tipo === 'Estoque') {
            setTipo('Representacao');
        }
    }, [hasModuloEstoque, tipo]);

    // --- Efeito para Controlar Checkbox com base no Tipo ---
    useEffect(() => {
        if (tipo === 'Representacao') {
            setMovimentarEstoque(false); // Força false
        } else if (tipo === 'Estoque') {
            // Se for estoque, idealmente deveria ler o default de novo ou manter o que user marcou?
            // Se for novo pedido, lemos o default no fetch acima.
            // Se o usuário MUDOU o tipo manualmente para Estoque, talvez devêssemos checar o default.
            // Simplificação: Se mudou para Estoque, ativa se tiver módulo.
            if (hasModuloEstoque && !isEditing) {
                // Re-checar config ou assumir true? Melhor assumir o da config que já carregamos?
                // Como não salvamos a config inteira no estado, vamos fazer uma lógica segura:
                // Se trocou p/ Estoque, e tem modulo, deixa usuario decidir, mas podemos iniciar true.
                // Vamos manter o valor que estava se não for Representacao.
            }
        }
    }, [tipo, hasModuloEstoque, isEditing]);

    // Carrega checkbox inicial baseado na config (executa uma vez se novo)
    useEffect(() => {
        if (!isEditing && hasModuloEstoque && tipo === 'Estoque') {
            apiCall('/api/configuracoes').then(c => {
                if (c && (c.movimentar_estoque_pedido === true || c.movimentar_estoque_pedido === 'true')) {
                    setMovimentarEstoque(true);
                }
            });
        }
    }, [isEditing, hasModuloEstoque, tipo, apiCall]); // Cuidado com loop, mas apiCall é estavel

    useEffect(() => {
        const produtoUsaGrade = produtoInfo && produtoInfo.tipo === 'Atacado' && produtoInfo.gradesList?.length > 0;

        if (!produtoUsaGrade) {
            setQtGrade(Number(quantidade) || 0);
            return;
        }

        if (!gradeSelecionada) {
            // Se a grade for des-selecionada, zera a qtGrade (ou mantém a de "quantidade")
            setQtGrade(Number(quantidade) || 0);
            return;
        }

        if (tamanhosConfigurados.length > 0) {
            const somaTamanhos = tamanhosConfigurados.reduce((sum, item) => sum + (Number(item.quantidade) || 0), 0);
            const totalCalculado = somaTamanhos * (Number(quantidade) || 1);
            setQtGrade(totalCalculado);
            return;
        }

        const gradeInfo = todasAsGrades.find(g => g.id === gradeSelecionada);
        if (gradeInfo && gradeInfo.quantidade_total > 0) {
            const totalCalculado = Number(gradeInfo.quantidade_total) * (Number(quantidade) || 1);
            setQtGrade(totalCalculado);
            return;
        }

        // Fallback caso a grade selecionada não tenha info (ainda carregando, talvez)
        setQtGrade(0);

    }, [quantidade, gradeSelecionada, produtoInfo, todasAsGrades, tamanhosConfigurados]);
    // Estados movidos para cima para corrigir ReferenceError
    const [isVisualizarModalVisible, setIsVisualizarModalVisible] = useState(false);

    const totalVendaItens = useMemo(() => itensDoPedido.reduce((acc, item) => acc + (item.subtotal || 0), 0), [itensDoPedido]);
    // Novo cálculo para "Valor Total" solicitado (Itens + Extras salvos)
    const valorTotalPedido = useMemo(() => {
        return totalVendaItens + Number(acrescimos || 0) + Number(frete || 0);
    }, [totalVendaItens, acrescimos, frete]);

    const valorGeradoReceber = useMemo(() => financeiroReceberList.reduce((acc, fin) => acc + (fin.valorTotal || 0), 0), [financeiroReceberList]);
    const valorRestante = useMemo(() => {
        // Cálculo simplificado: (Total do Pedido - Tudo que já foi gerado) + (Novos acréscimos/frete sendo inseridos agora)
        const saldoGeral = valorTotalPedido - valorGeradoReceber;
        return saldoGeral + Number(acrescimosInput || 0) + Number(freteInput || 0);
    }, [valorTotalPedido, valorGeradoReceber, acrescimosInput, freteInput]);




    const produtosComFornecedor = useMemo(() => {
        return produtos.map(p => {
            const fornecedor = fornecedores.find(f => f.id === p.fornecedor_id);
            const nomePrincipal = fornecedor ? fornecedor.nome : 'N/D';
            const numExtras = Number(p.num_fornecedores_extras || 0);
            return {
                ...p,
                fornecedorNome: numExtras > 0 ? 'Diversos' : nomePrincipal
            };
        });
    }, [produtos, fornecedores]);

    const aPagarPorFornecedor = useMemo(() => {
        // 1. Mapa base: Agrupa valores dos ITENS por fornecedor (Valor Total a Pagar)
        const mapaFornecedores = {};

        itensDoPedido.forEach(item => {
            if (!item.fornecedorId) return;
            const fid = String(item.fornecedorId);
            if (!mapaFornecedores[fid]) {
                mapaFornecedores[fid] = {
                    id: fid,
                    nome: item.fornecedorNome,
                    qtTotal: 0,
                    qtVol: 0,
                    valorTotalItens: 0, // Soma dos itens (Obrigação Total)
                    valorGerado: 0      // O que já foi lançado no financeiro
                };
            }
            mapaFornecedores[fid].qtTotal += (item.quantidadeTotal || 0);
            mapaFornecedores[fid].qtVol += (Number(item.quantidade) || 0);
            mapaFornecedores[fid].valorTotalItens += (item.quantidadeTotal || 0) * (item.precoCompra || 0);
        });

        // 2. Itera sobre TODAS as contas (Vinculadas + Avulsas/Despesas) para somar o que já foi gerado
        const todasAsContas = [...financeiroPagarList, ...outrasDespesasList];

        todasAsContas.forEach(fin => {
            if (!fin.fornecedorId) return;
            const fid = String(fin.fornecedorId);

            // Verifica vínculo estrito com o pedido
            const pertenceAoPedido =
                String(fin.pedidoId) === String(pedidoId) ||
                String(fin.numeroDoc) === String(numeroPedido) ||
                String(fin.numeroDoc) === String(pedidoId);

            if (pertenceAoPedido) {
                // SOMA APENAS SE O FORNECEDOR TIVER ITENS NO PEDIDO (Conforme solicitação)
                // Se não tiver itens, ignoramos na visualização de "Geração", 
                // pois o objetivo desta grid é mostrar o que precisa ser gerado para os itens atuais.
                if (mapaFornecedores[fid]) {
                    mapaFornecedores[fid].valorGerado += (fin.valorTotal || 0);
                }
            }
        });

        // 3. Retorna array com o cálculo final
        return Object.values(mapaFornecedores).map(f => ({
            ...f,
            valorRestante: Math.max(0, f.valorTotalItens - f.valorGerado)
        }));

    }, [itensDoPedido, financeiroPagarList, outrasDespesasList, pedidoId, numeroPedido]);


    // ================== CORREÇÃO AQUI ==================
    // 2. Definição do itensDoPedidoComStatus
    const itensDoPedidoComStatus = useMemo(() => {
        return itensDoPedido.map(item => {
            // CORREÇÃO: Respeita status Cancelado vindo do banco/backend
            if (item.status === 'Cancelado') {
                return item;
            }

            // 1. Identificar Vínculos
            const idRec = item.financeiroAReceber;
            const idPagar = item.financeiroAPagar;

            // 2. Buscar Objetos nas listas atuais (para ter status reativo)
            const recObj = idRec ? financeiroReceberList.find(f => String(f.id) === String(idRec)) : null;
            const pagarObj = idPagar ? financeiroPagarList.find(f => String(f.id) === String(idPagar)) : null;

            // 3. Verificar Existência e Pagamento
            const temRec = !!idRec;
            const temPagar = !!idPagar;

            const recPago = recObj && recObj.status === 'Pago';
            const pagarPago = pagarObj && pagarObj.status === 'Pago';

            // 4. Aplicar Regras
            // Regra 1: Nenhum financeiro gerado - Em aberto.
            if (!temRec && !temPagar) {
                return { ...item, status: 'Em aberto' };
            }

            // Regra 2: Gerado financeiro (default)
            let novoStatus = 'Faturado';

            if (temRec && temPagar) {
                // Ambos existem
                if (recPago && pagarPago) {
                    novoStatus = 'Pago'; // Regra 5: Tudo pago
                } else if (recPago) {
                    novoStatus = 'Pago Cliente'; // Regra 3: Somente a receber pago
                } else if (pagarPago) {
                    novoStatus = 'Pago Fornec.'; // Regra 4: Somente a pagar pago
                }
            } else if (temRec) {
                // Somente Receber existe
                if (recPago) {
                    novoStatus = 'Pago Cliente'; // Regra 3
                }
            } else if (temPagar) {
                // Somente Pagar existe
                if (pagarPago) {
                    novoStatus = 'Pago Fornec.'; // Regra 4
                }
            }

            return { ...item, status: novoStatus };
        });
    }, [itensDoPedido, financeiroReceberList, financeiroPagarList]);
    // ================== FIM DA CORREÇÃO ==================

    const cleanPhoneNumber = (phone) => {
        if (!phone) return '';
        return phone.replace(/\D/g, '');
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'Em Aberto': return 'badge-status-gray';
            case 'Faturado': return 'badge-status-yellow';
            case 'Pago Cliente': return 'badge-status-light-blue';
            case 'Pago Parcial': return 'badge-status-light-blue';
            case 'Pago Fornec.': return 'badge-status-light-blue';
            case 'Pago': return 'badge-status-dark-blue';
            case 'Enviado': return 'badge-status-light-green';
            case 'Concluído': return 'badge-status-dark-green';
            case 'Cancelado': return 'badge-status-red';
            default: return 'badge-status-gray';
        }
    };

    const handleClienteChange = (newClienteId) => {
        setClienteId(newClienteId);
        const clienteSelecionado = clientes.find(c => c.id === newClienteId);
        setTransportadoraId(clienteSelecionado?.transportadoraPadraoId || '');
        setEnderecoCliente(clienteSelecionado || null);
    };

    const handleNovaCor = async () => {
        if (!produtoSelecionadoId) {
            Swal.fire("Atenção", "Selecione um produto antes de adicionar uma cor.", "warning");
            return;
        }
        const { value: nomeCor } = await Swal.fire({
            title: "Nova Cor", input: "text", inputLabel: "Nome da nova cor",
            showCancelButton: true, confirmButtonText: "Salvar",
        });
        if (nomeCor) {
            try {
                await apiCall('/api/produto-cores', 'POST', {
                    produto_id: produtoSelecionadoId,
                    cor: nomeCor,
                    tenant_id: tenantId
                });
                setCoresDisponiveis([...coresDisponiveis, nomeCor]);
                setCorSelecionada(nomeCor);
                Swal.fire("Sucesso", "Cor adicionada!", "success");
            } catch (error) {
                console.error("Erro ao adicionar nova cor:", error);
                Swal.fire("Erro", error.message || "Não foi possível adicionar a nova cor.", "error");
            }
        }
    };

    const handleRemoverItem = (index) => {
        setItensDoPedido(itensDoPedido.filter((_, i) => i !== index));
    };

    const handleVisualizarGrade = (item) => {
        if (!item.tamanhosDoItem || item.tamanhosDoItem.length === 0) {
            Swal.fire("Atenção", "Este item não possui tamanhos configurados.", "info");
            return;
        }

        // Multiplica a quantidade da grade pela quantidade de volumes
        const multiplier = Number(item.quantidade) || 1;
        const tamanhosMultiplicados = item.tamanhosDoItem.map(t => ({
            ...t,
            quantidade: (Number(t.quantidade) || 0) * multiplier
        }));

        setTamanhosParaVisualizar(tamanhosMultiplicados);

        const gradeDesc = todasAsGrades.find(g => g.id === item.grade)?.descricao || 'Grade';

        setGradeVisualizarNome(gradeDesc);
        setIsVisualizarModalVisible(true);
    };

    const handleOpenTamanhoModal = () => {
        if (!gradeSelecionada) {
            Swal.fire("Atenção", "Selecione uma grade para configurar.", "warning");
            return;
        }
        if (tamanhosGradeAtual.length === 0) {
            // Tenta carregar sob demanda se estiver vazio
            handleGradeChange(gradeSelecionada).then(() => {
                // Após carregar, verifica de novo
                const gradeInfo = todasAsGrades.find(g => g.id === gradeSelecionada);
                if (gradeInfo && gradeInfo.tamanhos && gradeInfo.tamanhos.length > 0) {
                    setTamanhosGradeAtual(gradeInfo.tamanhos);
                    setTamanhosConfigurados(gradeInfo.tamanhos.map(t => ({ ...t })));
                    setIsTamanhoModalVisible(true);
                } else {
                    Swal.fire("Atenção", "Esta grade não possui tamanhos para configuração.", "warning");
                }
            });
            return;
        }

        // Se já tem tamanhos, apenas abre
        // CORREÇÃO: Só sobrescreve se ainda não tiver sido configurado nesta sessão de edição
        if (tamanhosConfigurados.length === 0) {
            setTamanhosConfigurados(tamanhosGradeAtual.map(t => ({ ...t })));
        }
        setIsTamanhoModalVisible(true);
    };

    const handleSaveTamanhos = (tamanhosEditados, somatorio) => {
        setTamanhosConfigurados(tamanhosEditados);
        // Atualiza a qtGrade total baseada no somatório * quantidade de volumes
        setQtGrade(somatorio * (Number(quantidade) || 1));
        setIsTamanhoModalVisible(false);
    };

    const handleGradeChange = useCallback(async (newGradeId) => {
        // 1. Converte o ID (que vem do <select> como string) para número
        const gradeId = Number(newGradeId);
        setGradeSelecionada(gradeId);

        // 2. Reseta os estados ANTES de carregar os novos dados
        setQtGrade(0);
        setTamanhosConfigurados([]);
        setTamanhosGradeAtual([]);

        if (!gradeId) {
            return; // Sai se o usuário des-selecionar (ex: "Selecione...")
        }

        try {
            // 3. BUSCA OS TAMANHOS E A ESTRUTURA COMPLETA NA API
            // Esta é a fonte da verdade sobre a grade
            const gradeCompletaComTamanhos = await apiCall(`/api/grades/${gradeId}`);

            if (!gradeCompletaComTamanhos || !gradeCompletaComTamanhos.tamanhos) {
                throw new Error("Falha ao buscar os tamanhos da grade na API.");
            }

            // 4. Pega os tamanhos padrão da grade (ex: [ {id: 1, t:38, q:1}, {id: 2, t:39, q:2} ])
            const tamanhosPadraoDaGrade = gradeCompletaComTamanhos.tamanhos || [];

            // 5. Salva a estrutura padrão (usada pelo modal caso o usuário clique)
            setTamanhosGradeAtual(tamanhosPadraoDaGrade);

            if (tamanhosPadraoDaGrade.length > 0) {

                // =============================================================
                // --- ESTA É A CORREÇÃO PRINCIPAL ---
                // Define os tamanhos configurados (para este item) como
                // sendo os tamanhos PADRÃO da grade.
                // Usamos .map(t => ({ ...t })) para garantir uma nova cópia.
                //
                // O useEffect que escuta [tamanhosConfigurados]
                // irá automaticamente recalcular o Qtde Total.
                // =============================================================
                setTamanhosConfigurados(tamanhosPadraoDaGrade.map(t => ({ ...t })));

            } else {
                Swal.fire("Atenção", "Esta grade não possui tamanhos configurados.", "warning");
                // Garante que está zerado se a grade não tiver tamanhos
                setTamanhosConfigurados([]);
            }
        } catch (error) {
            console.error("Erro ao carregar tamanhos da grade:", error);
            Swal.fire("Erro", error.message || "Não foi possível carregar os tamanhos da grade.", "error");
            // Limpa em caso de erro
            setTamanhosConfigurados([]);
        }
        // Atualiza as dependências, pois 'quantidade' e 'todasAsGrades' não são mais usadas aqui.
    }, [apiCall]);

    const handleAdicionarItem = () => {
        if (!produtoInfo) {
            Swal.fire("Atenção", "Selecione um produto e aguarde o carregamento dos detalhes.", "warning");
            return;
        }
        if (!produtoSelecionadoId || Number(quantidade) <= 0) {
            Swal.fire("Atenção", "Selecione um produto e informe a quantidade de volumes (caixas).", "warning");
            return;
        }

        const produtoControlaGrade = (produtoInfo.gradesList && produtoInfo.gradesList.length > 0);

        if (produtoControlaGrade) {
            if (!gradeSelecionada) {
                Swal.fire("Atenção", "Selecione uma grade para configurar o item.", "warning");
                return;
            }

            const somaTamanhosConfigurados = tamanhosConfigurados.reduce((sum, item) => sum + (Number(item.quantidade) || 0), 0);

            if (somaTamanhosConfigurados === 0) {
                Swal.fire("Atenção", "A grade está zerada. Configure as quantidades por tamanho clicando no ícone de caixa.", "warning");
                return;
            }
        }

        let quantidadeTotal;
        let tamanhosParaSalvar = [];

        if (produtoControlaGrade && gradeSelecionada) {
            // Se tem grade, a quantidade total é a soma dos tamanhos * n de volumes
            const somaTamanhos = tamanhosConfigurados.reduce((sum, item) => sum + (Number(item.quantidade) || 0), 0);
            quantidadeTotal = somaTamanhos * (Number(quantidade) || 1);
            tamanhosParaSalvar = tamanhosConfigurados.map(t => ({ ...t, quantidade: Number(t.quantidade) || 0 }));
        } else {
            // Se é item simples, a quantidade total é a "Qtde Vol."
            quantidadeTotal = Number(quantidade) || 0;
        }

        const subtotal = quantidadeTotal * (Number(precoVenda) || 0);

        const item = {
            produtoId: produtoSelecionadoId,
            descricao: produtoInfo.descricao,
            fornecedorNome,
            fornecedorId: fornecedorSelecionadoId || produtoInfo.fornecedor_id,
            cor: corSelecionada,
            grade: gradeSelecionada || null,
            quantidade: Number(quantidade) || 0, // Qtde Volumes (Caixas)
            quantidadeTotal, // Qtde Pares (Total)
            precoVenda: Number(precoVenda) || 0,
            precoCompra: Number(precoCompra) || 0,
            tipo: produtoInfo.tipo,
            qt_grade: 0, // Este campo parece não ser mais necessário como era antes
            subtotal,
            financeiroAReceber: null,
            financeiroAPagar: null,
            tamanhosDoItem: tamanhosParaSalvar,
        };
        setItensDoPedido([...itensDoPedido, item]);

        // Resetar campos
        setProdutoSelecionadoId(''); setProdutoInfo(null); setCorSelecionada('');
        setGradeSelecionada(''); setQuantidade(1); setPrecoVenda(0); setPrecoCompra(0);
        setTamanhosGradeAtual([]); setTamanhosConfigurados([]); setQtGrade(0);
        setFornecedorSelecionadoId(''); setFornecedorNome(''); // Resetar fornecedor
    };

    // --- NOVO: Handler para mudança de fornecedor ---
    const handleFornecedorChange = (e) => {
        const novoFornId = e.target.value;
        setFornecedorSelecionadoId(novoFornId);

        if (!produtoInfo) return;

        // Se limpou
        if (!novoFornId) {
            setFornecedorNome('');
            setPrecoCompra(0); // Ou mantem o original? Melhor zerar ou voltar ao default? Vamos voltar ao default do produto?
            // Mas se ele limpou, é melhor deixar em branco ou o original.
            // Para simplificar, vou buscar o preço original do produto principal se coincidir, senão zera
            return;
        }

        // Procura no fornecedor principal
        if (String(produtoInfo.fornecedor_id) === String(novoFornId)) {
            setFornecedorNome(produtoInfo.fornecedor_nome || 'Principal');
            setPrecoCompra(produtoInfo.valor_compra || 0);
            setPrecoVenda(produtoInfo.valor_venda || 0);
            return;
        }

        // Procura nos fornecedores extras
        const extra = (produtoInfo.fornecedoresList || []).find(f => String(f.fornecedor_id) === String(novoFornId));
        if (extra) {
            setFornecedorNome(extra.nome_fornecedor);
            setPrecoCompra(extra.valor_compra || 0);
            setPrecoVenda(extra.valor_venda || produtoInfo.valor_venda || 0);
        }
    };

    useEffect(() => {
        if (!tenantId || !currentUser) {
            setCarregando(true);
            return;
        }

        const buscarDadosGerais = async () => {
            setCarregando(true);
            try {
                // Usamos Promise.allSettled para garantir que todas as chamadas terminem,
                // mesmo que algumas falhem. Isso é mais robusto.
                const results = await Promise.allSettled([
                    apiCall(`/api/empresas?tipo=cliente`),      // 0
                    apiCall(`/api/empresas?tipo=transportadora`), // 1
                    apiCall(`/api/empresas?tipo=fornecedor`),   // 2
                    apiCall(`/api/produtos`),                     // 3
                    apiCall(`/api/empresas?tipo=terceiro`),     // 4
                    apiCall(`/api/grades?detalhes=true`)        // 5
                ]);

                // Função auxiliar para processar os resultados
                const processResult = (result, index) => {
                    if (result.status === 'fulfilled') {
                        return result.value || []; // Retorna os dados ou array vazio
                    }
                    // Loga o erro, mas não para a execução
                    console.warn(`Falha ao carregar o recurso ${index} (ex: tipo=terceiro):`, result.reason?.message || result.reason);
                    return []; // Retorna array vazio em caso de falha
                };

                // Mapeia os resultados para os estados
                const clientesData = processResult(results[0], 0);
                const transportadorasData = processResult(results[1], 1);
                const fornecedoresData = processResult(results[2], 2);
                const produtosData = processResult(results[3], 3);
                const terceirosData = processResult(results[4], 4);
                const gradesData = processResult(results[5], 5);

                setClientes(clientesData);
                setTransportadoras((transportadorasData).map(d => ({ id: d.id, nome: d.nome || d.nome_fantasia })));
                setFornecedores((fornecedoresData).map(d => ({ id: d.id, nome: d.nome || d.nome_fantasia })));
                setProdutos(produtosData);
                setTerceiros((terceirosData).map(d => ({ id: d.id, nome: d.nome || d.nome_fantasia })));
                setTodasAsGrades(gradesData);

            } catch (error) {
                // Este catch agora é para erros inesperados
                console.error("Erro Crítico no buscarDadosGerais:", error);
                Swal.fire("Erro Inesperado", "Ocorreu um erro inesperado ao carregar o formulário.", "error");
            } finally {
                setCarregando(false); // Para de carregar DEPOIS que tudo terminou
            }
        };

        buscarDadosGerais();

    }, [tenantId, currentUser, apiCall]); // Adicionado currentUser



    useEffect(() => {
        const newTotalVenda = itensDoPedido.reduce((acc, item) => acc + (item.subtotal || 0), 0);
        const newTotalCompra = itensDoPedido.reduce((acc, item) => acc + ((item.quantidadeTotal || 0) * (item.precoCompra || 0)), 0);
        const newTotalQtGrade = itensDoPedido.reduce((acc, item) => acc + (item.quantidadeTotal || 0), 0);
        const newTotalVol = itensDoPedido.reduce((acc, item) => acc + (Number(item.quantidade) || 0), 0);
        setTotalVenda(newTotalVenda);
        setTotalCompra(newTotalCompra);

        setTotalQtGrade(newTotalQtGrade);
        setTotalVol(newTotalVol);
    }, [itensDoPedido]);

    useEffect(() => {
        const buscarDetalhesProduto = async () => {
            if (!produtoSelecionadoId) {
                setProdutoInfo(null);
                setGradesDisponiveis([]);
                setCoresDisponiveis([]);
                setGradeSelecionada('');
                setQtGrade(0);
                setTamanhosConfigurados([]);
                setFornecedorNome('');
                setPrecoVenda(0);
                setPrecoCompra(0);
                return;
            }

            try {
                // Re-busca o produto para garantir dados frescos (opcional, mas seguro)
                const produtoCompleto = await apiCall(`/api/produtos/${produtoSelecionadoId}`);

                if (!produtoCompleto) {
                    setProdutoInfo(null);
                    setFornecedorNome('Produto não encontrado');
                    return;
                }

                const fornecedorDoProduto = fornecedores.find(f => f.id === produtoCompleto.fornecedor_id);
                setProdutoInfo(produtoCompleto);
                setPrecoVenda(produtoCompleto.valor_venda || 0);
                setPrecoCompra(produtoCompleto.valor_compra || 0);
                setFornecedorNome(fornecedorDoProduto?.nome || 'N/D');
                setFornecedorSelecionadoId(produtoCompleto.fornecedor_id || ''); // Auto-seleciona principal

                setCoresDisponiveis(produtoCompleto.colorsList || []);

                // Pega as grades vinculadas AO PRODUTO
                const gradesVinculadasIds = (produtoCompleto.gradesList || []).map(g => g.id);
                // Filtra a lista MESTRA de grades (que tem os tamanhos)
                const gradesCompletasVinculadas = todasAsGrades.filter(g => gradesVinculadasIds.includes(g.id));

                setGradesDisponiveis(gradesCompletasVinculadas);

                if (gradesCompletasVinculadas.length === 1) {
                    // Se só tem uma, auto-seleciona E chama o handleGradeChange
                    handleGradeChange(gradesCompletasVinculadas[0].id);
                } else {
                    setGradeSelecionada('');
                    setTamanhosConfigurados([]);
                    setQtGrade(0); // Zera qtGrade se não tem grade auto-selecionada
                }

            } catch (error) {
                console.error("Erro ao carregar detalhes do produto:", error);
                Swal.fire("Erro", "Não foi possível carregar os detalhes do produto.", "error");
                setProdutoInfo(null);
            }
        };

        buscarDetalhesProduto();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [produtoSelecionadoId, fornecedores, apiCall, todasAsGrades]); // Adicionado todasAsGrades


    const [localSaving, setLocalSaving] = useState(false);

    // Extraímos a lógica de submit para poder chamar programaticamente
    const handleStatusChange = (novoStatus) => {
        if (novoStatus === 'Cancelado') {
            const temFinanceiroReceber = financeiroReceberList && financeiroReceberList.length > 0;
            const temFinanceiroPagar = financeiroPagarList && financeiroPagarList.length > 0;

            if (temFinanceiroReceber || temFinanceiroPagar) {
                Swal.fire({
                    icon: 'error',
                    title: 'Ação Bloqueada',
                    text: 'Este pedido possui lançamentos financeiros gerados e não pode ser cancelado. Se realmente deseja cancelar este pedido, exclua os lançamentos financeiros e depois realize o cancelamento.',
                    confirmButtonColor: '#2185c6'
                });
                return;
            }
        }


        // VALIDAÇÃO: Não permitir "Pago" se houver financeiro em aberto
        if (novoStatus === 'Pago' || novoStatus === 'Concluído') {
            const temPendencia = financeiroReceberList.some(fin => fin.status !== 'Pago');
            if (temPendencia) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Ação Bloqueada',
                    text: 'Não é possível alterar o status para "Pago" ou "Concluído" pois existem lançamentos financeiros em aberto. Regularize o financeiro ou utilize "Pago Parcial".',
                    confirmButtonColor: '#f39c12'
                });
                return;
            }
        }

        setStatus(novoStatus);
    };

    const submitOrder = async (options = {}) => {
        if (loading || localSaving) return;

        // Suporte a overrides para valores calculados antes do setState processar
        const { overrides } = options;

        // VALIDAÇÃO DE CANCELAMENTO AO SALVAR
        if (status === 'Cancelado') {
            const temFinanceiroReceber = financeiroReceberList && financeiroReceberList.length > 0;
            const temFinanceiroPagar = financeiroPagarList && financeiroPagarList.length > 0;

            if (temFinanceiroReceber || temFinanceiroPagar) {
                Swal.fire({
                    icon: 'error',
                    title: 'Ação Bloqueada',
                    text: 'Este pedido possui lançamentos financeiros gerados e não pode ser cancelado. Se realmente deseja cancelar este pedido, exclua os lançamentos financeiros e depois realize o cancelamento.',
                    confirmButtonColor: '#2185c6'
                });
                return;
            }
        }

        setLocalSaving(true);
        try {
            const formData = {
                dataCadastro: dataCadastro,
                status: status,
                clienteId: clienteId,
                transportadoraId: transportadoraId,
                notaFiscal: notaFiscal,
                acrescimos: overrides?.acrescimos !== undefined ? Number(overrides.acrescimos) : Number(acrescimos || 0),
                frete: overrides?.frete !== undefined ? Number(overrides.frete) : Number(frete || 0),
                // Mapeia para o formato do banco ANTES de enviar
                itensDoPedido: itensDoPedido.map(mapToPostgresItem),
                totalVenda: totalVenda,
                movimentarEstoque: movimentarEstoque, // Envia flag para backend
                tipo: tipo, // Adiciona o tipo do pedido
            };

            // Passa options (como stayOnPage)

            // Passa options (como stayOnPage)
            // Se onSubmit for handleAtualizarPedido, ele pode retornar dados novos
            const result = await onSubmit(formData, options);
            return result;

        } catch (error) {
            console.error("Erro no submit do pedido:", error);
            // Mostrar erro para o usuário caso não tenha sido tratado antes
            await Swal.fire({
                icon: 'error',
                title: 'Erro ao Salvar',
                text: error.message || 'Ocorreu um erro ao salvar o pedido.',
                confirmButtonColor: '#d33'
            });
            // throw error; // Removido para evitar Uncaught Runtime Error se já foi tratado
        } finally {
            setLocalSaving(false);
            // Atualiza o estado inicial para o atual, resetando o isDirty após sucesso (se não houver recarga total)
            // Nota: Se o pai atualizar initialData, o useEffect lá em cima já cuida disso. 
            // Mas se não atualizar, precisamos garantir aqui.
            // Como não temos garantia do retorno exato do onSubmit atualizado em formato de 'snapshot', 
            // vamos confiar que se deu sucesso, o estado atual É o novo estado "limpo".
            setInitialDataState({
                dataCadastro: dataCadastro,
                status: status,
                clienteId: String(clienteId),
                transportadoraId: String(transportadoraId || ''),
                notaFiscal: String(notaFiscal || ''),
                acrescimos: Number(acrescimos || 0),
                frete: Number(frete || 0),
                itensDoPedido: JSON.parse(JSON.stringify(itensDoPedido))
            });
        }
    };



    const handleSubmit = async (event) => {
        event.preventDefault();
        await submitOrder();
    };

    const handleGerarFinanceiroReceber = async () => {
        if (processingAction) return;
        if (!appUserData || !appUserData['cadastrar_financeiro_a_receber']) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para gerar lançamentos a receber.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }
        if (valorRestante <= 0.001) { // Adicionada tolerância
            Swal.fire("Atenção", "O valor restante a gerar deve ser maior que zero.", "warning");
            return;
        }
        if (!isEditing || !pedidoId) {
            Swal.fire("Atenção", "O pedido precisa ser salvo antes de gerar financeiro.", "warning");
            return;
        }
        setProcessingAction(true);
        try {
            const dataCadastroAtual = new Date().toISOString();
            const dataVencimento = new Date();
            dataVencimento.setDate(new Date().getDate() + parseInt(prazoReceber || 0, 10));

            let docNumber = String(numeroPedido);
            let counter = 2;
            // Verifica se já existe na lista carregada para evitar conflito (gera 7-2, 7-3...)
            // CORREÇÃO: Verifica numeroDoc (do DB) ou numero_doc (recém-criado)
            const checkDocExists = (dNum) => financeiroReceberList.some(f => String(f.numeroDoc || f.numero_doc) === dNum);
            while (checkDocExists(docNumber)) {
                docNumber = `${numeroPedido}-${counter}`;
                counter++;
            }

            let freshItens = itensDoPedido;

            // Calcula novos totais para o Pedido
            const novoTotalAcrescimos = Number(acrescimos || 0) + Number(acrescimosInput || 0);
            const novoTotalFrete = Number(frete || 0) + Number(freteInput || 0);

            // --- AUTO-SAVE ANTES DE GERAR (Para garantir IDs dos itens e SALVAR TOTAIS) ---
            if (isEditing) {
                // Tenta salvar silenciosamente com os novos totais
                try {
                    const savedData = await submitOrder({
                        stayOnPage: true,
                        overrides: {
                            acrescimos: novoTotalAcrescimos,
                            frete: novoTotalFrete
                        }
                    });

                    // Atualiza estados locais de total com o que foi salvo
                    setAcrescimos(novoTotalAcrescimos);
                    setFrete(novoTotalFrete);

                    // Se retornou dados novos (esperado), atualiza a referência para usar os IDs novos
                    if (savedData && savedData.itensDoPedido) {
                        // Mapeia usando os helpers existentes
                        const mappedItems = savedData.itensDoPedido.map(item => ({
                            ...mapFromPostgresItem(item),
                            descricao: produtos.find(p => p.id === item.produto_id)?.descricao || 'N/A',
                            fornecedorNome: fornecedores.find(f => f.id === item.fornecedor_id)?.nome || 'N/A',
                        }));
                        freshItens = mappedItems;
                        setItensDoPedido(mappedItems); // Atualiza visualmente também
                    }
                } catch (err) {
                    console.error("Erro no auto-save:", err);
                    Swal.fire("Erro", "Não foi possível salvar as alterações do pedido antes de gerar o financeiro. Tente salvar manualmente.", "error");
                    return;
                }
            }
            // ---------------------------------------------------------------


            const docData = {
                tenant_id: tenantId,
                cliente_id: clienteId,
                pedido_id: pedidoId,
                valor_total: valorRestante,
                forma_pagamento: formaPagamentoReceber,
                status: 'Em aberto',
                data_cadastro: dataCadastroAtual,
                data_vencimento: dataVencimento.toISOString().split('T')[0],
                data_pagamento: null,
                valor_subtotal: valorRestante,
                // Para o financeiro, salva apenas a PARTE incremental gerada agora
                outras_despesas_total: Number(acrescimosInput || 0),
                frete: Number(freteInput || 0),
                prazo: parseInt(prazoReceber || 0, 10),
                numero_doc: docNumber, // Usa o número calculado
                descricao: 'Origem: Pedido de venda'
            };

            // Pega IDs dos itens que AINDA NÃO foram faturados
            // USA "freshItens" pois pode ter itens recém-criados com IDs vindos do save
            const itensNaoFaturadosIds = freshItens
                .filter(item => !item.financeiroAReceber)
                .map(item => item.id); // Agora garantimos que têm ID

            const newReceber = await apiCall(`/api/financeiro/receber`, 'POST', {
                lancamento: docData,
                itens_nao_faturados: itensNaoFaturadosIds
            });

            // Atualiza os itens no estado
            setItensDoPedido(prevItens =>
                prevItens.map(item => ({
                    ...item,
                    financeiroAReceber: item.financeiroAReceber || (newReceber.itensAtualizados.map(String).includes(String(item.id)) ? newReceber.id : null)
                }))
            );

            // Adiciona o novo lançamento à lista
            setFinanceiroReceberList([...financeiroReceberList, {
                id: newReceber.id,
                ...docData,
                valorTotal: docData.valor_total,
                formaPagamento: docData.forma_pagamento,
                dataVencimento: docData.data_vencimento,
                dataCadastro: dataCadastroAtual
            }]);

            // ATUALIZA STATUS DO PEDIDO PARA FATURADO SE AINDA ESTIVER EM ABERTO
            if (status === 'Em Aberto' || status === 'Em aberto') {
                setStatus('Faturado');
            }

            Swal.fire("Sucesso", "Financeiro a receber gerado!", "success");
            // Limpa as entradas incrementais
            setAcrescimosInput('');
            setFreteInput('');
        } catch (error) {
            console.error("Erro ao gerar financeiro:", error);
            Swal.fire("Erro", error.message || "Não foi possível gerar o financeiro.", "error");
        } finally {
            setProcessingAction(false);
        }
    };

    const handleGerarFinanceiroPagar = async (fornecedor, valorAGerar) => {
        if (processingAction) return;
        if (!appUserData || !appUserData['cadastrar_financeiro_a_pagar']) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para gerar lançamentos a pagar.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }
        if (!isEditing || !pedidoId) {
            Swal.fire("Atenção", "O pedido precisa ser salvo antes de gerar financeiro.", "warning");
            return;
        }
        if (valorAGerar <= 0.001) { // Adicionada tolerância
            Swal.fire("Atenção", "O valor a gerar deve ser maior que zero.", "warning");
            return;
        }

        let freshItens = itensDoPedido;

        // --- AUTO-SAVE ANTES DE GERAR A PAGAR ---
        if (isEditing) {
            try {
                const savedData = await submitOrder({ stayOnPage: true });
                if (savedData && savedData.itensDoPedido) {
                    const mappedItems = savedData.itensDoPedido.map(item => ({
                        ...mapFromPostgresItem(item),
                        descricao: produtos.find(p => p.id === item.produto_id)?.descricao || 'N/A',
                        fornecedorNome: fornecedores.find(f => f.id === item.fornecedor_id)?.nome || 'N/A',
                    }));
                    freshItens = mappedItems;
                    setItensDoPedido(mappedItems);
                }
            } catch (err) {
                console.error("Erro no auto-save pagar:", err);
                Swal.fire("Erro", "Não foi possível salvar as alterações. Tente manualmente.", "error");
                return;
            }
        }
        // ----------------------------------------

        setGerandoPagarId(fornecedor.id);
        setProcessingAction(true);
        try {
            const dataCadastroAtual = new Date().toISOString();
            const dataVencimento = new Date();
            dataVencimento.setDate(new Date().getDate() + parseInt(prazoPagar || 0, 10));

            let docNumber = String(numeroPedido);
            let counter = 2;
            // Lógica de sufixo para Pagar também
            // CORREÇÃO: Verifica numeroDoc (do DB) ou numero_doc (recém-criado)
            const checkPagarExists = (dNum) => financeiroPagarList.some(f => String(f.numeroDoc || f.numero_doc) === dNum);
            while (checkPagarExists(docNumber)) {
                docNumber = `${numeroPedido}-${counter}`;
                counter++;
            }

            const docData = {
                tenant_id: tenantId,
                fornecedor_id: fornecedor.id,
                pedido_id: pedidoId,
                valor_total: valorAGerar,
                status: 'Em aberto',
                data_cadastro: dataCadastroAtual,
                data_vencimento: dataVencimento.toISOString().split('T')[0],
                forma_pagamento: formaPagamentoPagar,
                data_pagamento: null,
                valor_subtotal: valorAGerar,
                prazo: parseInt(prazoPagar || 0, 10),
                numero_doc: docNumber, // Usa o número calculado com sufixo
                descricao: 'Origem: Pedido de venda'
            };

            const itensParaVincular = freshItens
                .filter(item => String(item.fornecedorId) === String(fornecedor.id) && !item.financeiroAPagar)
                .map(item => item.id);

            // console.log("Itens para vincular:", itensParaVincular, "Fornecedor:", fornecedor.id);

            if (itensParaVincular.length === 0) {
                const confirmacao = await Swal.fire({
                    title: 'Atenção',
                    text: 'Não foram encontrados itens deste fornecedor pendentes de vínculo. Deseja gerar o financeiro mesmo assim (sem vincular itens)?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Sim, gerar',
                    cancelButtonText: 'Cancelar'
                });
                if (!confirmacao.isConfirmed) return;
            }

            const newPagar = await apiCall(`/api/financeiro/pagar`, 'POST', {
                lancamento: docData,
                itens_do_fornecedor: itensParaVincular
            });

            const novoLancamento = {
                id: newPagar.id,
                pedidoId: docData.pedido_id,
                fornecedorId: docData.fornecedor_id,
                fornecedorNome: fornecedor.nome,
                valorTotal: docData.valor_total,
                formaPagamento: docData.forma_pagamento,
                status: docData.status,
                dataCadastro: docData.data_cadastro,
                dataVencimento: docData.data_vencimento,
                dataPagamento: docData.data_pagamento,
                descricao: docData.descricao || '',
            };

            setFinanceiroPagarList(currentList => [...currentList, novoLancamento]);

            setItensDoPedido(prevItens =>
                prevItens.map(item =>
                    String(item.fornecedorId) === String(fornecedor.id) && (newPagar.itensAtualizados.map(String).includes(String(item.id)))
                        ? { ...item, financeiroAPagar: newPagar.id }
                        : item
                )
            );

            await Swal.fire("Sucesso", `Financeiro para ${fornecedor.nome} gerado!`, "success");

            // ATUALIZA STATUS DO PEDIDO PARA FATURADO SE AINDA ESTIVER EM ABERTO
            if (status === 'Em Aberto' || status === 'Em aberto') {
                setStatus('Faturado');
            }

        } catch (error) {
            console.error("Erro ao gerar financeiro a pagar:", error);
            await Swal.fire("Erro", error.message || "Não foi possível gerar o financeiro a pagar.", "error");
        } finally {
            setGerandoPagarId(null);
            setProcessingAction(false);
        }
    };
    const handleExcluirFinanceiroReceber = async (id) => {
        if (processingAction) return;
        if (!appUserData || !appUserData['excluir_financeiro_a_receber']) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para excluir lançamentos a receber.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }

        const lancamento = financeiroReceberList.find(f => f.id === id);
        if (lancamento && lancamento.status === 'Pago') {
            Swal.fire({
                icon: 'warning',
                title: 'Atenção',
                text: 'Este lançamento está pago e não pode ser excluído. Se deseja excluir, estorne o pagamento primeiro.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }

        setProcessingAction(true);
        try {
            // Pede ao backend para desvincular os itens
            await apiCall(`/api/financeiro/receber/${id}`, 'DELETE', {
                itens_afetados: itensDoPedido
                    .filter(item => item.financeiroAReceber === id)
                    .map(item => item.id)
            });

            // Atualiza o estado local
            setItensDoPedido(prevItens => prevItens.map(item =>
                // CORREÇÃO: Comparação segura (string vs number)
                String(item.financeiroAReceber) === String(id) ? { ...item, financeiroAReceber: null } : item
            ));
            setFinanceiroReceberList(financeiroReceberList.filter(fin => fin.id !== id));
            Swal.fire("Excluído", "Lançamento removido.", "info");
        } catch (error) {
            console.error("Erro ao excluir:", error);
            Swal.fire("Erro", error.message || "Não foi possível remover o lançamento.", "error");
        } finally {
            setProcessingAction(false);
        }
    };

    const handlePagarFinanceiroReceber = async (id) => {
        if (processingAction) return;
        if (!appUserData || !appUserData['pagar_financeiro_a_receber']) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para receber pagamentos.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }
        try {
            const dataPagamento = new Date().toISOString();
            await apiCall(`/api/financeiro/receber/${id}/pagar`, 'PATCH', { data_pagamento: dataPagamento });

            const novaListaReceber = financeiroReceberList.map(fin => fin.id === id ? { ...fin, status: 'Pago', dataPagamento } : fin);
            setFinanceiroReceberList(novaListaReceber);
            Swal.fire("Sucesso", "Lançamento pago!", "success");

            // Lógica de verificação completa de Status
            const todosReceberPagos = novaListaReceber.every(f => f.status === 'Pago');
            const todosPagarPagos = financeiroPagarList.every(f => f.status === 'Pago');
            const todasDespesasPagas = outrasDespesasList.every(f => f.status === 'Pago');

            const temReceber = novaListaReceber.length > 0;
            const temPagar = financeiroPagarList.length > 0 || outrasDespesasList.length > 0;

            if (temReceber && !todosReceberPagos) {
                // Se tem receber pendente, não pode ser Pago nem Pago Cliente completo
                // Mantém status atual ou Faturado
            } else if (temReceber && todosReceberPagos) {
                if (!temPagar) {
                    // Só tem receber e está pago -> Pago ou Pago Cliente (vamos de Pago Cliente pois é venda)
                    // Mas o usuário pediu "Pago" se tudo estiver pago. Se não tem pagar, tudo é receber.
                    // Vamos seguir a regra: Tudo pago -> Pago.
                    setStatus('Pago');
                } else {
                    if (todosPagarPagos && todasDespesasPagas) {
                        setStatus('Pago');
                    } else {
                        setStatus('Pago Cliente');
                    }
                }
            } else if (!temReceber && temPagar) {
                // Só tem pagar (compra/despesa)
                if (todosPagarPagos && todasDespesasPagas) {
                    setStatus('Pago'); // ou Pago Fornec? Se tudo tá pago, é Pago.
                } else {
                    // Pendente
                }
            }

            // Simplificando a regra pedida:
            // 1. TUDO PAGO (Receber + Pagar + Despesas) -> 'Pago'
            // 2. APENAS Receber Pago (e existe pagar em aberto) -> 'Pago Cliente'
            // 3. APENAS Pagar Pago (e existe receber em aberto) -> 'Pago Fornec.'

            const receivingDone = (!temReceber || todosReceberPagos);
            const payingDone = (!temPagar || (todosPagarPagos && todasDespesasPagas));

            if (receivingDone && payingDone) {
                setStatus('Pago');
            } else if (receivingDone && !payingDone) {
                setStatus('Pago Cliente');
            } else if (!receivingDone && payingDone) {
                setStatus('Pago Fornec.');
            }
        } catch (error) {
            console.error("Erro ao pagar:", error);
            Swal.fire("Erro", error.message || "Não foi possível registrar o pagamento.", "error");
        } finally {
            setProcessingAction(false);
        }
    };

    const handleEstornarFinanceiroReceber = async (id) => {
        if (processingAction) return;
        if (!appUserData || !appUserData['estornar_financeiro_a_receber']) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para estornar recebimentos.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }
        setProcessingAction(true);
        try {
            await apiCall(`/api/financeiro/receber/${id}/estornar`, 'PATCH');
            setFinanceiroReceberList(financeiroReceberList.map(fin => fin.id === id ? { ...fin, status: 'Em aberto', dataPagamento: null } : fin));
            Swal.fire("Estornado", "Pagamento estornado.", "info");
        } catch (error) {
            console.error("Erro ao estornar:", error);
            Swal.fire("Erro", error.message || "Não foi possível estornar o pagamento.", "error");
        } finally {
            setProcessingAction(false);
        }
    };

    const handleExcluirFinanceiroPagar = async (id) => {
        if (processingAction) return;
        if (!appUserData || !appUserData['excluir_financeiro_a_pagar']) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para excluir lançamentos a pagar.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }

        const lancamento = financeiroPagarList.find(f => f.id === id) || outrasDespesasList.find(d => d.id === id);
        if (lancamento && lancamento.status === 'Pago') {
            Swal.fire({
                icon: 'warning',
                title: 'Atenção',
                text: 'Este lançamento está pago e não pode ser excluído. Se deseja excluir, estorne o pagamento primeiro.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }

        setProcessingAction(true);
        try {
            const itensAfetados = itensDoPedido
                .filter(item => item.financeiroAPagar === id)
                .map(item => item.id);

            await apiCall(`/api/financeiro/pagar/${id}`, 'DELETE', {
                itens_afetados: itensAfetados
            });

            setItensDoPedido(prevItens => prevItens.map(item =>
                item.financeiroAPagar === id ? { ...item, financeiroAPagar: null } : item
            ));
            setFinanceiroPagarList(financeiroPagarList.filter(fin => fin.id !== id));
            setOutrasDespesasList(outrasDespesasList.filter(fin => fin.id !== id)); // Remove de ambas as listas
            Swal.fire("Excluído", "Lançamento removido.", "info");
        } catch (error) {
            console.error("Erro ao excluir:", error);
            Swal.fire("Erro", error.message || "Não foi possível remover o lançamento.", "error");
        } finally {
            setProcessingAction(false);
        }
    };

    const handlePagarFinanceiroPagar = async (id) => {
        if (processingAction) return;
        if (!appUserData || !appUserData['pagar_financeiro_a_pagar']) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para efetuar pagamentos.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }
        try {
            const dataPagamento = new Date().toISOString();
            await apiCall(`/api/financeiro/pagar/${id}/pagar`, 'PATCH', { data_pagamento: dataPagamento });
            // Atualiza em ambas as listas
            const novaListaPagar = financeiroPagarList.map(fin => fin.id === id ? { ...fin, status: 'Pago', dataPagamento } : fin);
            const novaListaDespesas = outrasDespesasList.map(desp => desp.id === id ? { ...desp, status: 'Pago', dataPagamento } : desp);

            setFinanceiroPagarList(novaListaPagar);
            setOutrasDespesasList(novaListaDespesas);
            Swal.fire("Sucesso", "Lançamento pago!", "success");

            // Lógica de verificação completa de Status
            const todosReceberPagos = financeiroReceberList.every(f => f.status === 'Pago');
            const todosPagarPagos = novaListaPagar.every(f => f.status === 'Pago');
            const todasDespesasPagas = novaListaDespesas.every(f => f.status === 'Pago');

            const temReceber = financeiroReceberList.length > 0;
            const temPagar = novaListaPagar.length > 0 || novaListaDespesas.length > 0;

            const receivingDone = (!temReceber || todosReceberPagos);
            const payingDone = (!temPagar || (todosPagarPagos && todasDespesasPagas));

            if (receivingDone && payingDone) {
                setStatus('Pago');
            } else if (receivingDone && !payingDone) {
                setStatus('Pago Cliente');
            } else if (!receivingDone && payingDone) {
                setStatus('Pago Fornec.');
            }
        } catch (error) {
            console.error("Erro ao pagar:", error);
            Swal.fire("Erro", error.message || "Não foi possível registrar o pagamento.", "error");
        } finally {
            setProcessingAction(false);
        }
    };

    const handleEditAcrescimosManual = async () => {
        const { value } = await Swal.fire({
            title: 'Editar Acréscimos',
            text: 'Informe o valor total de acréscimos para este pedido',
            input: 'number',
            inputValue: acrescimos || 0,
            showCancelButton: true,
            confirmButtonText: 'Salvar',
            cancelButtonText: 'Cancelar'
        });

        if (value !== undefined && value !== null) {
            setAcrescimos(value);
        }
    };

    const handleEditFreteManual = async () => {
        const { value } = await Swal.fire({
            title: 'Editar Frete',
            text: 'Informe o valor total de frete para este pedido',
            input: 'number',
            inputValue: frete || 0,
            showCancelButton: true,
            confirmButtonText: 'Salvar',
            cancelButtonText: 'Cancelar'
        });

        if (value !== undefined && value !== null) {
            setFrete(value);
        }
    };

    const handleEstornarFinanceiroPagar = async (id) => {
        if (processingAction) return;
        if (!appUserData || !appUserData['estornar_financeiro_a_pagar']) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para estornar pagamentos.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }
        setProcessingAction(true);
        try {
            await apiCall(`/api/financeiro/pagar/${id}/estornar`, 'PATCH');
            // Atualiza em ambas as listas
            setFinanceiroPagarList(financeiroPagarList.map(fin => fin.id === id ? { ...fin, status: 'Em aberto', dataPagamento: null } : fin));
            setOutrasDespesasList(outrasDespesasList.map(desp => desp.id === id ? { ...desp, status: 'Em aberto', dataPagamento: null } : desp));
            Swal.fire("Estornado", "Pagamento estornado.", "info");
        } catch (error) {
            console.error("Erro ao estornar:", error);
            Swal.fire("Erro", error.message || "Não foi possível estornar o pagamento.", "error");
        } finally {
            setProcessingAction(false);
        }
    };

    const handleGerarOutraDespesa = async () => {
        if (processingAction) return;
        if (!appUserData || !appUserData['cadastrar_financeiro_a_pagar']) { // Usando permissão de pagar genérica ou específica se houver? Usarei a de cadastrar pagar.
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para lançar despesas (Contas a Pagar).',
                confirmButtonColor: '#2185c6'
            });
            return;
        }
        if (!despesaTerceiroId || despesaValor <= 0) {
            Swal.fire("Atenção", "Preencha todos os campos da despesa.", "warning");
            return;
        }
        if (!isEditing || !pedidoId) {
            Swal.fire("Atenção", "O pedido precisa ser salvo antes de gerar despesa.", "warning");
            return;
        }
        setProcessingAction(true);
        try {
            const docData = {
                tenant_id: tenantId,
                fornecedor_id: despesaTerceiroId,
                pedido_id: pedidoId,
                descricao: despesaDescricao || `Origem: Despesa de pedido de venda ${numeroPedido}`,
                valor_total: Number(despesaValor),
                status: 'Em aberto',
                data_cadastro: new Date(despesaData + "T12:00:00").toISOString(),
                data_pagamento: null,
                valor_subtotal: Number(despesaValor), // Adicionando subtotal
            };

            const newDespesa = await apiCall(`/api/financeiro/pagar/despesa`, 'POST', docData);

            const terceiro = terceiros.find(t => String(t.id) === String(despesaTerceiroId));
            setOutrasDespesasList([...outrasDespesasList, {
                id: newDespesa.id,
                fornecedorId: despesaTerceiroId,
                fornecedorNome: terceiro?.nome || 'N/A',
                descricao: despesaDescricao || `Origem: Despesa de pedido de venda ${numeroPedido}`,
                valorTotal: Number(despesaValor),
                status: 'Em aberto',
                dataCadastro: new Date(despesaData + "T12:00:00").toISOString(),
                dataPagamento: null,
                // valorTotal: Number(despesaValor) // Duplicado, removido
            }]);
            setDespesaTerceiroId(''); setDespesaDescricao(''); setDespesaValor(0);
            Swal.fire("Sucesso", "Despesa gerada com sucesso!", "success");
        } catch (error) {
            console.error("Erro ao gerar despesa:", error);
            Swal.fire("Erro", error.message || "Não foi possível gerar a despesa.", "error");
        } finally {
            setProcessingAction(false);
        }
    };




    // --- JSX FINAL ---

    if (carregando) {
        return <div className="loading-container"><div className="loading-spinner"></div><p>Carregando...</p></div>;
    }

    return (
        <div className="pedidos-content">
            {/* Modais mantidos fora do fluxo principal se necessário, ou dentro */}
            {isTamanhoModalVisible &&
                <TamanhoModal
                    isVisible={isTamanhoModalVisible}
                    onClose={() => setIsTamanhoModalVisible(false)}
                    tamanhosData={tamanhosConfigurados.length > 0 ? tamanhosConfigurados : tamanhosGradeAtual}
                    onSave={handleSaveTamanhos}
                    gradeNome={todasAsGrades.find(g => g.id === gradeSelecionada)?.descricao || 'Grade'}
                />
            }
            {isVisualizarModalVisible &&
                <VisualizarGradeModal
                    isVisible={isVisualizarModalVisible}
                    onClose={() => setIsVisualizarModalVisible(false)}
                    tamanhosData={tamanhosParaVisualizar}
                    gradeNome={gradeVisualizarNome}
                />
            }

            <form onSubmit={handleSubmit}>
                <div className="d-flex justify-content-between align-items-end mb-0">
                    <ul className="nav nav-tabs m-0">
                        <li className="nav-item">
                            <button type="button" className={`nav-link ${activeTab === 'dados' ? 'active' : ''}`} onClick={() => setActiveTab('dados')}>Dados do Pedido</button>
                        </li>
                        <li className="nav-item">
                            <button type="button" className={`nav-link ${activeTab === 'entrega' ? 'active' : ''}`} onClick={() => setActiveTab('entrega')}>Entrega</button>
                        </li>
                        <li className="nav-item" title={!isEditing ? "Salve o pedido para habilitar o faturamento" : ""}>
                            <button
                                type="button"
                                className={`nav-link ${activeTab === 'faturamento' ? 'active' : ''} ${!isEditing ? 'disabled' : ''}`}
                                onClick={() => isEditing && setActiveTab('faturamento')}
                            >
                                Faturamento
                            </button>
                        </li>
                        <li className="nav-item" title={!isEditing ? "Salve o pedido para habilitar as despesas" : ""}>
                            <button
                                type="button"
                                className={`nav-link ${activeTab === 'despesas' ? 'active' : ''} ${!isEditing ? 'disabled' : ''}`}
                                onClick={() => isEditing && setActiveTab('despesas')}
                            >
                                Despesas
                            </button>
                        </li>
                    </ul>
                </div>

                <div className="tab-content mb-3">
                    {/* --- ABA DADOS --- */}
                    <div className={`tab-pane fade ${activeTab === 'dados' ? 'show active' : ''}`}>
                        <div className="row">
                            <div className="col-lg-9">
                                <div className="form-card">
                                    <h5 className="form-section-header">Dados Básicos</h5>
                                    <div className="row g-3">
                                        <div className="col-md-1">
                                            <label className="form-label-custom">Número</label>
                                            <input type="text" className="form-control-custom bg-light" value={numeroPedido || ''} disabled />
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label-custom">Tipo</label>
                                            <select className="form-control-custom" value={tipo} onChange={e => setTipo(e.target.value)} disabled={!hasModuloEstoque}>
                                                <option value="Representacao">Representação</option>
                                                <option value="Estoque">Estoque</option>
                                            </select>
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label-custom">Data</label>
                                            <input type="date" className="form-control-custom" value={dataCadastro} onChange={e => setDataCadastro(e.target.value)} />
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label-custom">Status</label>
                                            <select className="form-control-custom" value={status} onChange={e => handleStatusChange(e.target.value)}>
                                                <option value="Em Aberto">Em Aberto</option>
                                                <option value="Faturado">Faturado</option>
                                                <option value="Pago Cliente">Pago Cliente</option>
                                                <option value="Pago Parcial">Pago Parcial</option>
                                                <option value="Pago Fornec.">Pago Fornec.</option>
                                                <option value="Pago">Pago</option>
                                                <option value="Enviado">Enviado</option>
                                                <option value="Concluído">Concluído</option>
                                                <option value="Cancelado">Cancelado</option>
                                            </select>
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label-custom">Nota Fiscal</label>
                                            <input type="text" className="form-control-custom" value={notaFiscal} onChange={e => setNotaFiscal(e.target.value)} />
                                        </div>
                                        {hasModuloEstoque && tipo === 'Estoque' && (
                                            <div className="col-md-3 d-flex align-items-end mb-2">
                                                <div className="form-check form-switch mb-1 ms-2">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id="movimentarEstoqueCheck"
                                                        checked={movimentarEstoque}
                                                        onChange={(e) => setMovimentarEstoque(e.target.checked)}
                                                    />
                                                    <label className="form-check-label small" htmlFor="movimentarEstoqueCheck">
                                                        Movimentar Estoque
                                                    </label>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="row g-3 mt-1">
                                        <div className="col-md-8">
                                            <label className="form-label-custom">Cliente</label>
                                            <select className="form-control-custom" value={clienteId} onChange={e => handleClienteChange(e.target.value)} required>
                                                <option value="">Selecione...</option>
                                                {clientes.map(c => <option key={c.id} value={c.id}>{c.nome || c.nomeFantasia}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-md-4 d-flex align-items-center">
                                            {enderecoCliente && (
                                                <div className="text-muted small ms-2 ps-3 border-start">
                                                    {enderecoCliente.cidade && enderecoCliente.uf ?
                                                        <div className="mb-1"><i className="fas fa-map-marker-alt me-2 text-primary"></i>{enderecoCliente.cidade} - {enderecoCliente.uf}</div> : null}
                                                    {enderecoCliente.telefone ?
                                                        <div>
                                                            <i className="fas fa-phone me-2 text-primary"></i>{enderecoCliente.telefone}
                                                            <a href={`https://wa.me/55${cleanPhoneNumber(enderecoCliente.telefone)}`} target="_blank" rel="noopener noreferrer" className="ms-2 text-success"><i className="fab fa-whatsapp"></i></a>
                                                        </div> : null}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="form-card form-card-action mt-1">
                                    <div className="row g-3">
                                        <div className={isPlanoRepresentante ? "col-md-6" : "col-md-9"}>
                                            <label className="form-label-custom form-label-large">Adicionar Produtos</label>
                                            <select className="form-control-custom" value={produtoSelecionadoId} onChange={e => setProdutoSelecionadoId(e.target.value)}>
                                                <option value="">Selecione...</option>
                                                {produtosComFornecedor.map(p => (<option key={p.id} value={p.id}>{`${p.codigo} - ${p.descricao} - ${p.fornecedorNome}`}</option>))}
                                            </select>
                                        </div>
                                        {produtoInfo && isPlanoRepresentante && (
                                            <div className="col-md-3">
                                                <label className="form-label-custom">Fornecedor</label>
                                                <select
                                                    className="form-select-custom"
                                                    value={fornecedorSelecionadoId}
                                                    onChange={handleFornecedorChange}
                                                    disabled={!produtoInfo}
                                                >
                                                    <option value="">Selecione...</option>
                                                    {/* Fornecedor Principal */}
                                                    {produtoInfo && produtoInfo.fornecedor_id && (
                                                        <option value={produtoInfo.fornecedor_id}>
                                                            {produtoInfo.fornecedor_nome || 'Padrão'}
                                                        </option>
                                                    )}
                                                    {/* Fornecedores Extras */}
                                                    {produtoInfo && produtoInfo.fornecedoresList && produtoInfo.fornecedoresList.map(f => (
                                                        <option key={f.fornecedor_id} value={f.fornecedor_id}>
                                                            {f.nome_fornecedor}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                        {produtoInfo && (
                                            <div className="col-md-3">
                                                <label className="form-label-custom">Cor</label>
                                                <div className="input-group">
                                                    <select className="form-control-custom form-select" value={corSelecionada} onChange={e => setCorSelecionada(e.target.value)}>
                                                        <option value="">Selecione...</option>
                                                        {coresDisponiveis.map(c => <option key={c} value={c}>{c}</option>)}
                                                    </select>
                                                    <button className="btn btn-outline-primary-custom" type="button" onClick={handleNovaCor}>+</button>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Linha de Grade e Valores */}
                                    <div className="row g-3 mt-1">
                                        {produtoInfo && (
                                            produtoInfo.tipo === 'Atacado' && produtoInfo.gradesList?.length > 0 ? (
                                                <>
                                                    <div className="col-md-3">
                                                        <label className="form-label-custom">Grade</label>
                                                        <div className="input-group">
                                                            <select className="form-control-custom form-select" value={gradeSelecionada} onChange={e => handleGradeChange(e.target.value)} disabled={gradesDisponiveis.length === 1}>
                                                                <option value="">Selecione...</option>
                                                                {gradesDisponiveis.map(g => (<option key={g.id} value={g.id}>{g.descricao}</option>))}
                                                            </select>
                                                            <button type="button" className="btn btn-outline-primary-custom" onClick={handleOpenTamanhoModal} disabled={!gradeSelecionada}>
                                                                <i className="fa-solid fa-box-open"></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-2">
                                                        <label className="form-label-custom">Qtde Vol.</label>
                                                        <input
                                                            type="number"
                                                            className="form-control-custom"
                                                            value={quantidade}
                                                            onChange={e => {
                                                                const val = e.target.value;
                                                                // Permite digitar, mas ao usar valor numérico, garante inteiro
                                                                // Mas para UX fluida, apenas valida no onBlur ou previne ponto?
                                                                // Vamos prevenir ponto para garantir inteiro visualmente
                                                                if (val.includes('.') || val.includes(',')) return;
                                                                setQuantidade(Number(val));
                                                            }}
                                                            min="1"
                                                            step="1"
                                                            onKeyDown={(e) => {
                                                                if (e.key === '.' || e.key === ',') {
                                                                    e.preventDefault();
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="col-md-2">
                                                        <label className="form-label-custom">Qtde Pares</label>
                                                        <input type="number" className="form-control-custom bg-light" value={qtGrade} disabled />
                                                    </div>
                                                    {isPlanoRepresentante && (
                                                        <div className="col-md-2">
                                                            <label className="form-label-custom">Vlr. Compra</label>
                                                            <input type="number" step="0.01" className="form-control-custom" value={precoCompra} onChange={e => setPrecoCompra(Number(e.target.value))} />
                                                        </div>
                                                    )}
                                                    <div className="col-md-3">
                                                        <label className="form-label-custom">Vlr. Venda</label>
                                                        <div className="d-flex gap-2">
                                                            <input type="number" step="0.01" className="form-control-custom" value={precoVenda} onChange={e => setPrecoVenda(Number(e.target.value))} />
                                                            <button type="button" className="btn btn-primary-custom" onClick={handleAdicionarItem}>Adicionar</button>
                                                        </div>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="col-md-6" />
                                                    <div className="col-md-2">
                                                        <label className="form-label-custom">Qtde</label>
                                                        <input
                                                            type="number"
                                                            className="form-control-custom"
                                                            value={quantidade}
                                                            onChange={e => setQuantidade(e.target.value)}
                                                            min="0.01"
                                                            step="0.01"
                                                        />
                                                    </div>
                                                    <div className="col-md-4">
                                                        <label className="form-label-custom">Vlr. Venda</label>
                                                        <div className="d-flex gap-2">
                                                            <input type="number" step="0.01" className="form-control-custom" value={precoVenda} onChange={e => setPrecoVenda(Number(e.target.value))} />
                                                            <button type="button" className="btn btn-primary-custom" onClick={handleAdicionarItem}>Adicionar</button>
                                                        </div>
                                                    </div>
                                                </>
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Resumo Lateral */}
                            <div className="col-lg-3">
                                <div className="form-card sticky-top" style={{ top: '100px', zIndex: 10 }}>
                                    <h5 className="form-section-header">Resumo</h5>

                                    <div className="d-flex justify-content-between mb-2"><span>Volumes:</span> <strong>{totalVol}</strong></div>
                                    <div className="d-flex justify-content-between mb-2"><span>Quantidade:</span> <strong>{totalQtGrade}</strong></div>

                                    <div className="d-flex justify-content-between mb-2"><span>Valor Subtotal:</span> {totalVenda.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>

                                    <div className="d-flex justify-content-between mb-2 text-primary">
                                        <span>Acréscimos:</span>
                                        <span>
                                            {Number(acrescimos || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                            <button type="button" className="btn btn-link btn-sm p-0 ms-2 text-decoration-none" onClick={handleEditAcrescimosManual} title="Editar manualmente">
                                                <i className="fas fa-pencil-alt"></i>
                                            </button>
                                        </span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2 text-primary">
                                        <span>Frete:</span>
                                        <span>
                                            {Number(frete || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                            <button type="button" className="btn btn-link btn-sm p-0 ms-2 text-decoration-none" onClick={handleEditFreteManual} title="Editar manualmente">
                                                <i className="fas fa-pencil-alt"></i>
                                            </button>
                                        </span>
                                    </div>

                                    <hr />
                                    <div className="d-flex justify-content-between mb-2 fw-bold fs-5">
                                        <span>Valor Total:</span>
                                        <span>{(totalVenda + (Number(acrescimos) || 0) + (Number(frete) || 0)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                                    </div>
                                    <hr />

                                    {isPlanoRepresentante && (
                                        <div className="d-flex justify-content-between mb-2 text-muted"><span>Total Compra:</span> {totalCompra.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
                                    )}
                                    <div className="d-flex justify-content-between mb-2"><span>Total Venda:</span> <strong>{totalVenda.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong></div>

                                    {isPlanoRepresentante && (
                                        <>
                                            <hr />
                                            <div className="d-flex justify-content-between fw-bold text-success">
                                                <span>Lucro:</span>
                                                {(totalVenda - totalCompra).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* LISTA DE ITENS */}
                        {itensDoPedido.length > 0 && (
                            <div className="form-card mt-1">
                                <h5 className="form-section-header">Itens do Pedido</h5>
                                <div className="table-responsive" style={{ overflowX: 'auto', display: 'block' }}>
                                    <table className="table-custom" style={{ minWidth: '100%', width: 'max-content' }}>
                                        <thead>
                                            <tr>
                                                <th className="text-center" style={{ minWidth: '100px' }}>Ações</th>
                                                <th style={{ minWidth: '200px' }}>Produto</th>
                                                {isPlanoRepresentante && <th style={{ minWidth: '150px' }}>Fornecedor</th>}
                                                <th style={{ minWidth: '100px' }}>Cor</th>
                                                <th style={{ minWidth: '120px' }}>Grade</th>
                                                <th style={{ minWidth: '60px' }}>Vol.</th>
                                                <th style={{ minWidth: '60px' }}>Qtd.</th>
                                                {isPlanoRepresentante && <th style={{ minWidth: '110px' }}>Vlr. Compra</th>}
                                                <th style={{ minWidth: '110px' }}>Vlr. Venda</th>
                                                <th style={{ minWidth: '110px' }}>Total</th>
                                                <th style={{ minWidth: '120px' }}>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {itensDoPedidoComStatus.map((item, index) => {
                                                const gradeDesc = todasAsGrades.find(g => g.id === item.grade)?.descricao || item.grade || '';
                                                return (
                                                    <tr key={item.id || index}>
                                                        <td data-label="Ações" className="text-center">
                                                            <div className="actions-cell">
                                                                {item.tamanhosDoItem && item.tamanhosDoItem.length > 0 && (
                                                                    <button type="button" className="btn-action-sm btn-edit" title="Visualizar grade" onClick={() => handleVisualizarGrade(item)}>
                                                                        <i className="fa-solid fa-box-open"></i>
                                                                    </button>
                                                                )}
                                                                <button type="button" className="btn-action-sm btn-delete" onClick={() => handleRemoverItem(index)} disabled={item.status !== 'Em aberto'} title="Remover">
                                                                    <i className="fas fa-trash"></i>
                                                                </button>
                                                            </div>
                                                        </td>
                                                        <td data-label="Produto">{item.descricao}</td>
                                                        {isPlanoRepresentante && <td data-label="Fornecedor">{item.fornecedorNome}</td>}
                                                        <td data-label="Cor">{item.cor || '-'}</td>
                                                        <td data-label="Grade">{gradeDesc}</td>
                                                        <td data-label="Vol.">{item.quantidade}</td>
                                                        <td data-label="Qtd.">{item.quantidadeTotal}</td>
                                                        {isPlanoRepresentante && <td data-label="Vlr. Compra">{(item.precoCompra || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>}
                                                        <td data-label="Vlr. Venda">{(item.precoVenda || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                                                        <td data-label="Total" className="fw-bold">{(item.subtotal || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                                                        <td data-label="Status"><span className={`pedidos-badge ${getStatusBadgeClass(item.status)}`}>{item.status}</span></td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* --- ABA ENTREGA --- */}
                    <div className={`tab-pane fade ${activeTab === 'entrega' ? 'show active' : ''}`}>
                        <div className="form-card">
                            <h5 className="form-section-header">Dados de Entrega</h5>
                            <div className="row g-3">
                                <div className="col-12">
                                    <label className="form-label-custom">Transportadora</label>
                                    <select className="form-control-custom" value={transportadoraId} onChange={e => setTransportadoraId(e.target.value)}>
                                        <option value="">Selecione...</option>
                                        {transportadoras.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
                                    </select>
                                </div>
                            </div>
                            <h5 className="form-section-header mt-4">Endereço de Entrega</h5>
                            {enderecoCliente ? (
                                <div className="row g-3">
                                    <div className="col-md-2"><label className="form-label-custom">CEP</label><input type="text" className="form-control-custom bg-light" value={enderecoCliente.cep || ''} disabled /></div>
                                    <div className="col-md-5"><label className="form-label-custom">Rua</label><input type="text" className="form-control-custom bg-light" value={enderecoCliente.rua || ''} disabled /></div>
                                    <div className="col-md-2"><label className="form-label-custom">Número</label><input type="text" className="form-control-custom bg-light" value={enderecoCliente.numero || ''} disabled /></div>
                                    <div className="col-md-3"><label className="form-label-custom">Bairro</label><input type="text" className="form-control-custom bg-light" value={enderecoCliente.bairro || ''} disabled /></div>
                                    <div className="col-md-4"><label className="form-label-custom">Cidade</label><input type="text" className="form-control-custom bg-light" value={enderecoCliente.cidade || ''} disabled /></div>
                                    <div className="col-md-2"><label className="form-label-custom">UF</label><input type="text" className="form-control-custom bg-light" value={enderecoCliente.uf || ''} disabled /></div>
                                    <div className="col-md-6"><label className="form-label-custom">Complemento</label><input type="text" className="form-control-custom bg-light" value={enderecoCliente.complemento || ''} disabled /></div>
                                </div>
                            ) : (
                                <p className="text-muted">Selecione um cliente na aba "Dados do Pedido" para visualizar o endereço.</p>
                            )}
                        </div>
                    </div>

                    {/* --- ABA FATURAMENTO --- */}
                    {isEditing && (
                        <div className={`tab-pane fade ${activeTab === 'faturamento' ? 'show active' : ''}`}>
                            <ul className="nav nav-tabs mb-3">
                                <li className="nav-item">
                                    <button
                                        type="button"
                                        className={`nav-link ${faturamentoTab === 'receber' ? 'active' : ''}`}
                                        onClick={() => setFaturamentoTab('receber')}
                                    >
                                        A Receber
                                    </button>
                                </li>
                                {isPlanoRepresentante && tipo !== 'Estoque' && (
                                    <li className="nav-item">
                                        <button
                                            type="button"
                                            className={`nav-link ${faturamentoTab === 'pagar' ? 'active' : ''}`}
                                            onClick={() => setFaturamentoTab('pagar')}
                                        >
                                            A Pagar
                                        </button>
                                    </li>
                                )}
                            </ul>

                            {/* Conteúdo Faturamento (Simplificado para manter foco visual, lógica mantida) */}
                            <div className="tab-content">
                                <div className={`tab-pane fade ${faturamentoTab === 'receber' ? 'show active' : ''}`}>
                                    <div className="form-card form-card-action">
                                        <h5 className="form-section-header">Financeiro a Receber</h5>
                                        <div className="row g-3">
                                            <div className="col-md"><CurrencyInput label="Valor Total" value={valorTotalPedido} disabled /></div>
                                            <div className="col-md"><label className="form-label-custom">Acréscimos</label><input type="number" step="0.01" className="form-control-custom" value={acrescimosInput} onChange={e => setAcrescimosInput(e.target.value)} /></div>
                                            <div className="col-md"><label className="form-label-custom">Frete</label><input type="number" step="0.01" className="form-control-custom" value={freteInput} onChange={e => setFreteInput(e.target.value)} /></div>
                                            <div className="col-md"><CurrencyInput label="Valor Gerado" value={valorGeradoReceber} disabled /></div>
                                            <div className="col-md"><CurrencyInput label="Valor Restante" value={valorRestante} disabled /></div>
                                        </div>
                                        <div className="row g-3 align-items-end mt-2">
                                            <div className="col-md-3">
                                                <label className="form-label-custom">Forma de Pag.</label>
                                                <select className="form-control-custom" value={formaPagamentoReceber} onChange={e => setFormaPagamentoReceber(e.target.value)}>
                                                    <option>PIX</option><option>Dinheiro</option><option>Cartão de Crédito</option><option>Cartão de Débito</option><option>Boleto</option>
                                                </select>
                                            </div>
                                            <div className="col-md-2">
                                                <label className="form-label-custom">Prazo (dias)</label><input type="number" className="form-control-custom" value={prazoReceber} onChange={e => setPrazoReceber(e.target.value)} />
                                            </div>
                                            <div className="col-md-4">
                                                <button type="button" className="btn btn-primary-custom w-100" onClick={handleGerarFinanceiroReceber} disabled={valorRestante <= 0.001 || processingAction}>
                                                    {processingAction && faturamentoTab === 'receber' ? <span className="spinner-border spinner-border-sm me-2"></span> : 'Gerar Lançamento'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Tabela Receber */}
                                    {financeiroReceberList.length > 0 && (
                                        <div className="form-card mt-3">
                                            <h5 className="form-section-header">Lançamentos Gerados</h5>
                                            <div className="table-responsive">
                                                <table className="table-custom">
                                                    <thead><tr><th>Forma</th><th>Vencimento</th><th>Data Pag.</th><th>Valor</th><th>Status</th><th className="text-center">Ações</th></tr></thead>
                                                    <tbody>
                                                        {financeiroReceberList.map(fin => (
                                                            <tr key={fin.id}>
                                                                <td data-label="Forma">{fin.formaPagamento}</td>
                                                                <td data-label="Vencimento">{formatarDataSegura(fin.dataVencimento)}</td>
                                                                <td data-label="Data Pag.">{formatarDataSegura(fin.dataPagamento)}</td>
                                                                <td data-label="Valor">{(fin.valorTotal || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                                                                <td data-label="Status"><span className={`pedidos-badge ${fin.status === 'Pago' ? 'badge-success' : 'badge-warning'}`}>{fin.status}</span></td>
                                                                <td data-label="Ações" className="text-center">
                                                                    <div className="actions-cell d-flex justify-content-center gap-2">
                                                                        {fin.status === 'Pago' ? (
                                                                            <button type="button" className="btn-action-sm btn-edit" title="Estornar" onClick={() => handleEstornarFinanceiroReceber(fin.id)} disabled={processingAction}><i className="fas fa-undo"></i></button>
                                                                        ) : (
                                                                            <button type="button" className="btn-action-sm btn-edit" title="Pagar" onClick={() => handlePagarFinanceiroReceber(fin.id)} disabled={processingAction}><i className="fas fa-check"></i></button>
                                                                        )}
                                                                        <button type="button" className="btn-action-sm btn-delete" onClick={() => handleExcluirFinanceiroReceber(fin.id)} disabled={processingAction}><i className="fas fa-trash"></i></button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* A Pagar omitido para brevidade, mas segue lógica similar se necessário. Se não, manter o existente. */}
                                {/* A Pagar omitido para brevidade, mas segue lógica similar se necessário. Se não, manter o existente. */}
                                {isPlanoRepresentante && tipo !== 'Estoque' && (
                                    <div className={`tab-pane fade ${faturamentoTab === 'pagar' ? 'show active' : ''}`}>
                                        <div className="form-card form-card-action">
                                            <h5 className="form-section-header">Financeiro a Pagar por Fornecedor</h5>
                                            <div className="row g-3 align-items-end mb-4">
                                                <div className="col-md-4">
                                                    <label className="form-label-custom">Forma de Pagamento</label>
                                                    <select className="form-control-custom form-select" value={formaPagamentoPagar} onChange={e => setFormaPagamentoPagar(e.target.value)}>
                                                        <option>PIX</option>
                                                        <option>Boleto</option>
                                                        <option>Transferência</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-2">
                                                    <label className="form-label-custom">Prazo (dias)</label>
                                                    <input type="number" className="form-control-custom" value={prazoPagar} onChange={e => setPrazoPagar(e.target.value)} placeholder="0" min="0" />
                                                </div>
                                            </div>

                                            <div className="table-responsive">
                                                <table className="table-custom">
                                                    <thead>
                                                        <tr>
                                                            <th>Fornecedor</th>
                                                            <th>Valor Total</th>
                                                            <th>Valor Gerado</th>
                                                            <th>Valor Restante</th>
                                                            <th className="text-center">Ação</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {aPagarPorFornecedor.map(forn => (
                                                            <tr key={forn.id}>
                                                                <td data-label="Fornecedor">{forn.nome}</td>
                                                                <td data-label="Valor Total"><strong>{(forn.valorTotalItens || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong></td>
                                                                <td data-label="Valor Gerado">{(forn.valorGerado || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                                                                <td data-label="Valor Restante" className={forn.valorRestante > 0 ? "text-danger fw-bold" : "text-success fw-bold"}>
                                                                    {(forn.valorRestante || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                                                </td>
                                                                <td data-label="Ação" className="text-center">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-primary-custom btn-sm"
                                                                        onClick={() => handleGerarFinanceiroPagar(forn, forn.valorRestante)}
                                                                        disabled={gerandoPagarId === forn.id || (forn.valorRestante || 0) <= 0.001 || processingAction}
                                                                    >
                                                                        {gerandoPagarId === forn.id ? (
                                                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                        ) : (
                                                                            'Gerar'
                                                                        )}
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        {financeiroPagarList.length > 0 && (
                                            <div className="form-card mt-3">
                                                <h5 className="form-section-header">Lançamentos Gerados a Pagar</h5>
                                                <div className="table-responsive">
                                                    <table className="table-custom">
                                                        <thead>
                                                            <tr>
                                                                <th>Fornecedor</th>
                                                                <th>Forma Pag.</th>
                                                                <th>Vencimento</th>
                                                                <th>Valor</th>
                                                                <th>Status</th>
                                                                <th>Data Pag.</th>
                                                                <th className="text-center">Ações</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {financeiroPagarList.map(fin => (
                                                                <tr key={fin.id}>
                                                                    <td data-label="Fornecedor">{fin.fornecedorNome}</td>
                                                                    <td data-label="Forma Pag.">{fin.formaPagamento}</td>
                                                                    <td data-label="Vencimento">{formatarDataSegura(fin.dataVencimento)}</td>
                                                                    <td data-label="Valor"><strong>{fin.valorTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong></td>
                                                                    <td data-label="Status"><span className={`pedidos-badge ${fin.status === 'Pago' ? 'badge-success' : 'badge-warning'}`}>{fin.status}</span></td>
                                                                    <td data-label="Data Pag.">{formatarDataSegura(fin.dataPagamento)}</td>
                                                                    <td data-label="Ações" className="text-center">
                                                                        <div className="actions-cell d-flex justify-content-center gap-2">
                                                                            {fin.status === 'Pago' ? (
                                                                                <button type="button" className="btn-action-sm btn-edit" title="Estornar" onClick={() => handleEstornarFinanceiroPagar(fin.id)} disabled={processingAction}><i className="fas fa-undo"></i></button>
                                                                            ) : (
                                                                                <button type="button" className="btn-action-sm btn-edit" title="Pagar" onClick={() => handlePagarFinanceiroPagar(fin.id)} disabled={processingAction}><i className="fas fa-check"></i></button>
                                                                            )}
                                                                            <button type="button" className="btn-action-sm btn-delete" title="Excluir" onClick={() => handleExcluirFinanceiroPagar(fin.id)} disabled={processingAction}><i className="fas fa-trash"></i></button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* --- ABA OUTRAS DESPESAS --- */}
                    {isEditing && (
                        <div className={`tab-pane fade ${activeTab === 'despesas' ? 'show active' : ''}`}>
                            <div className="form-card form-card-action">
                                <h5 className="form-section-header">Lançar Despesa</h5>
                                <div className="row g-3 align-items-end">
                                    <div className="col-md-4"><label className="form-label-custom">Terceiro</label><select className="form-control-custom" value={despesaTerceiroId} onChange={e => setDespesaTerceiroId(e.target.value)}><option value="">Selecione...</option>{terceiros.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}</select></div>
                                    <div className="col-md-4"><label className="form-label-custom">Descrição</label><input type="text" className="form-control-custom" value={despesaDescricao} onChange={e => setDespesaDescricao(e.target.value)} /></div>
                                    <div className="col-md-2"><label className="form-label-custom">Valor</label><input type="number" step="0.01" className="form-control-custom" value={despesaValor} onChange={e => setDespesaValor(e.target.value)} /></div>
                                    <div className="col-md-2">
                                        <button type="button" className="btn btn-primary-custom w-100" onClick={handleGerarOutraDespesa} disabled={processingAction}>
                                            {processingAction && activeTab === 'despesas' ? <span className="spinner-border spinner-border-sm me-2"></span> : 'Adicionar'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            {outrasDespesasList.length > 0 && (
                                <div className="form-card mt-3">
                                    <h5 className="form-section-header">Despesas Lançadas</h5>
                                    <table className="table-custom">
                                        <thead><tr><th>Terceiro</th><th>Descrição</th><th>Valor</th><th className="text-center">Ações</th></tr></thead>
                                        <tbody>
                                            {outrasDespesasList.map(d => (
                                                <tr key={d.id}>
                                                    <td data-label="Terceiro">{d.fornecedorNome}</td><td data-label="Descrição">{d.descricao}</td><td data-label="Valor">{d.valorTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                                                    <td data-label="Ações" className="text-center">
                                                        <div className="actions-cell d-flex justify-content-center gap-2">
                                                            {d.status === 'Pago' ? (
                                                                <button type="button" className="btn-action-sm btn-edit" title="Estornar" onClick={() => handleEstornarFinanceiroPagar(d.id)} disabled={processingAction}><i className="fas fa-undo"></i></button>
                                                            ) : (
                                                                <button type="button" className="btn-action-sm btn-edit" title="Pagar" onClick={() => handlePagarFinanceiroPagar(d.id)} disabled={processingAction}><i className="fas fa-check"></i></button>
                                                            )}
                                                            <button type="button" className="btn-action-sm btn-delete" onClick={() => handleExcluirFinanceiroPagar(d.id)} disabled={processingAction}><i className="fas fa-trash"></i></button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer Fixo */}
                <div className="form-actions-bar">
                    <Link to='/app/pedidos' className="btn-secondary-custom me-2">Cancelar</Link>
                    <button
                        type="submit"
                        className="btn-primary-custom"
                        disabled={loading || localSaving || (isEditing && !isDirty)}
                        style={{ opacity: (loading || localSaving || (isEditing && !isDirty)) ? 0.6 : 1, cursor: (loading || localSaving || (isEditing && !isDirty)) ? 'not-allowed' : 'pointer' }}
                    >
                        {(loading || localSaving) ? (
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        ) : (isEditing && !isDirty) ? (
                            <i className="fas fa-check me-2"></i>
                        ) : (
                            <i className="fas fa-save me-2"></i>
                        )}
                        {loading || localSaving ? 'Salvando...' : (isEditing && !isDirty ? 'Salvo' : 'Salvar Pedido')}
                    </button>
                </div>
            </form >
        </div >
    );
}
export default PedidoForm;